import React, { useState, useEffect, useRef, useCallback } from "react";
import API_URL, {
  equipmentDrawingsListPath,
  equipmentDrawingDeletePath,
  equipmentDrawingRenamePath,
} from "../Config";
import axiosInstance from "../axiosinstance.js";
import PaginationFooter from "../Components/PaginationFooter.js";
import { TbFileImport, TbFileExport } from "react-icons/tb";
import { FiFilter } from "react-icons/fi";
import { IoEye } from "react-icons/io5";
import { QRCodeCanvas } from "qrcode.react";
import { LiaQrcodeSolid } from "react-icons/lia";
import ClearIcon from "@mui/icons-material/Clear";
import { LuTicketSlash } from "react-icons/lu";
import { LuTickets } from "react-icons/lu";
import { LuFileText } from "react-icons/lu";
import { ToastContainer, toast, Bounce } from "react-toastify";
import SearchBar from "../Components/SearchBar.js";
import EquipmentDrawingUploadModal, {
  canManageEquipmentDrawings,
} from "../Components/EquipmentDrawingUploadModal.js";
import DrawingPreviewModal from "../Components/DrawingPreviewModal.js";
import TicketModalTextFields from "../Components/TicketModalTextFields.js";
import { formatDrawingMeta } from "../utils/drawingMeta.js";
import "../Components/jeconDialog.css";
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon,
  EditOutlined as EditIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckBox as CheckBoxIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Lock as LockIcon,
  PauseRounded as HoldIcon,
} from "@mui/icons-material";
import { withMandatoryDepartments } from "../utils/mandatoryAssignees";
import MUISelect from "@mui/material/Select";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import {
  Snackbar,
  Alert as MuiAlert,
  TableRow,
  TableCell,
  TextField,
  InputAdornment,
  Tooltip,
  Popover,
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { FiSearch } from "react-icons/fi";
import { IoIosClose } from "react-icons/io";
import { MdOutlineRefresh } from "react-icons/md";
import { useNavigate, useLocation } from "react-router-dom";
import "./Equipment.css";
import "./QrModal.css";
import "./TicketModal.css";

const EQUIPMENT_LIST_STATE_KEY = "equipment_list_state_v1";
const EQUIPMENT_TOAST_OPTIONS = { containerId: "main" };
const cleanUnknownResources = (resources = []) => resources.filter((resource) => {
  const name = String(resource?.source_resource_name || "").trim().toLowerCase();
  return name && !["nan", "none", "null"].includes(name);
});

const formatHoldTimestamp = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  })
    .format(date)
    .replace(/\b(am|pm)\b/i, (part) => part.toUpperCase());
};

const TICKET_PRIORITY_OPTIONS = [
  { value: "Low", color: "#2e7d32" },
  { value: "Medium", color: "#b45309" },
  { value: "High", color: "#e65100" },
  { value: "Critical", color: "#d32f2f" },
];

const getTicketUserDepartments = (user) =>
  Array.isArray(user?.departments)
    ? user.departments.map((department) => department?.name).filter(Boolean)
    : [];

const getTicketUserDepartmentLabel = (user) => {
  const departments = getTicketUserDepartments(user);
  if (departments.length === 0) return "No department";
  if (departments.length === 1) return departments[0];
  return `${departments[0]} +${departments.length - 1}`;
};

const getTicketUserDepartmentText = (user) => {
  const departments = getTicketUserDepartments(user);
  return departments.length ? `Dept: ${departments.join(", ")}` : "No department";
};

const getTicketUserChipLabel = (user) => {
  if (!user?.name) return "";
  const departmentLabel = getTicketUserDepartmentLabel(user);
  return departmentLabel === "No department"
    ? user.name
    : `${user.name} (${departmentLabel})`;
};

const getTicketUserSearchText = (user) =>
  [user?.name, user?.email, ...getTicketUserDepartments(user)]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const filterTicketAssigneeUsers = (options, { inputValue }) => {
  const query = inputValue.trim().toLowerCase();
  if (!query) return options;
  return options.filter((option) =>
    getTicketUserSearchText(option).includes(query),
  );
};

const filterTicketDepartments = (options, { inputValue }) => {
  const query = inputValue.trim().toLowerCase();
  if (!query) return options;
  return options.filter((option) =>
    [option?.name, option?.code]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(query),
  );
};

const TICKET_AUTOCOMPLETE_SLOT_PROPS = {
  popper: {
    placement: "bottom-start",
    modifiers: [{ name: "flip", enabled: false }],
  },
  paper: {
    sx: {
      mt: 0.5,
      border: "1px solid #d8dee9",
      borderRadius: 2,
      boxShadow: "0 14px 34px rgba(15, 23, 42, 0.14)",
      overflow: "hidden",
    },
  },
  listbox: {
    sx: {
      maxHeight: 180,
      py: 0.5,
      "& .MuiAutocomplete-option": {
        color: "#1f2937",
        transition: "background-color 0.12s ease, color 0.12s ease",
      },
      "& .MuiAutocomplete-option.Mui-focused": {
        bgcolor: "#fff7f7",
      },
      "& .MuiAutocomplete-option[aria-selected='true']": {
        bgcolor: "#fff1f2",
        color: "#7f1d1d",
      },
      "& .MuiAutocomplete-option[aria-selected='true'].Mui-focused": {
        bgcolor: "#ffe4e6",
      },
    },
  },
};

// Triage status chips. Values must match the backend `status` filter param.
const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "delayed", label: "Delayed" },
  { value: "not_started", label: "Not Started" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
];

const HOLD_FILTERS = [
  { value: "all", label: "All hold states" },
  { value: "on_hold", label: "On Hold" },
  { value: "not_on_hold", label: "Not On Hold" },
];

const Equipment = ({ isSidebarExpanded }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin =
    (localStorage.getItem("role_name") || "").toLowerCase() === "admin";
  const [equipments, setEquipments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [equipmentGroupOptions, setEquipmentGroupOptions] = useState([]);
  const [selectedEquipmentGroup, setSelectedEquipmentGroup] = useState(null);
  const [pendingGroupCode, setPendingGroupCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [scheduleExporting, setScheduleExporting] = useState(false);
  const isInitialLoad = useRef(true);
  const [error, setError] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  // Per-status record counts come from the main equipment list API response so
  // the filter chips do not need their own fan-out requests.
  const [statusCounts, setStatusCounts] = useState({});
  const [holdCounts, setHoldCounts] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [statusFilter, setStatusFilter] = useState(() => {
    try {
      // An explicit ?status= param (e.g. dashboard "Top Delayed" shortcut) is the
      // navigation intent and takes precedence over the persisted filter, so the
      // first paint already shows the requested filter instead of flickering.
      const queryStatus = String(
        new URLSearchParams(window.location.search).get("status") || "",
      )
        .trim()
        .toLowerCase();
      if (queryStatus && STATUS_FILTERS.some((f) => f.value === queryStatus)) {
        return queryStatus;
      }

      const savedRaw = sessionStorage.getItem(EQUIPMENT_LIST_STATE_KEY);
      const saved = savedRaw ? JSON.parse(savedRaw) : null;
      const currentProject = JSON.parse(localStorage.getItem("selectedProject")) || [];
      const currentProjectId = currentProject?.id || currentProject;
      const canRestore =
        !String(saved?.projectId || "") ||
        String(saved?.projectId || "") === String(currentProjectId || "");
      if (canRestore && STATUS_FILTERS.some((f) => f.value === saved?.statusFilter)) {
        return saved.statusFilter;
      }
    } catch {}
    return "all";
  });
  const [holdStatusFilter, setHoldStatusFilter] = useState(() => {
    try {
      const queryValue = String(
        new URLSearchParams(window.location.search).get("hold_status") || "",
      ).trim().toLowerCase();
      if (HOLD_FILTERS.some((filter) => filter.value === queryValue)) return queryValue;
      const saved = JSON.parse(sessionStorage.getItem(EQUIPMENT_LIST_STATE_KEY) || "null");
      if (HOLD_FILTERS.some((filter) => filter.value === saved?.holdStatusFilter)) {
        return saved.holdStatusFilter;
      }
    } catch {}
    return "all";
  });
  const [importResponse, setImportResponse] = useState(null);
  const [resourceMappingImport, setResourceMappingImport] = useState(null);
  const [resourceMappingSelections, setResourceMappingSelections] = useState({});

  const [snackbar, setSnackbar] = useState({
    show: false,
    message: "",
    type: "",
  });
  const [isDragging, setIsDragging] = useState(false);
  const isStatusPinnedRef = useRef(false);
  // Ensures the persisted list state (page/filters) is restored from
  // sessionStorage only once, on mount — not on every location.search change.
  const hasRestoredSavedStateRef = useRef(false);
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const accepted = Array.from(files).filter(
        (file) =>
          file.type.startsWith("image/") || file.type.startsWith("video/"),
      );
      if (accepted.length === 0) {
        toast.error(
          "Please upload image or video files only",
          EQUIPMENT_TOAST_OPTIONS,
        );
        return;
      }
      if (accepted.length < files.length) {
        toast.error(
          "Some files were skipped — only images and videos are allowed",
          EQUIPMENT_TOAST_OPTIONS,
        );
      }
      setTicketAttachments((prev) => [...prev, ...accepted]);
    }
  };
  const handleCloseTicketModal = () => {
    setShowTicketModal(false);
    // Reset all fields
    ticketTitleRef.current = "";
    ticketDescriptionRef.current = "";
    setTicketType("Missing Information");
    setTicketPriority("Medium");
    setTicketAssigneeUsers([]);
    setTicketAssigneeDepartments([]);
    setTicketAttachments([]);
    setTicketErrors({});
    setSelectedEquipmentId(null);
  };
  const [selectedProject, setSelectedProject] = useState(() => {
    return JSON.parse(localStorage.getItem("selectedProject")) || [];
  });
  const [selectedEquipmentId, setSelectedEquipmentId] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const ticketTitleRef = useRef("");
  const ticketDescriptionRef = useRef("");
  const [ticketType, setTicketType] = useState("Missing Information");
  const [ticketPriority, setTicketPriority] = useState("Medium");
  const [ticketAssigneeUsers, setTicketAssigneeUsers] = useState([]);
  const [ticketAssigneeDepartments, setTicketAssigneeDepartments] = useState([]);
  const [ticketAttachments, setTicketAttachments] = useState([]);
  const [ticketErrors, setTicketErrors] = useState({});
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [assignableDepartments, setAssignableDepartments] = useState([]);
  const [assignablesLoading, setAssignablesLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showAreaPopup, setShowAreaPopup] = useState(false);
  const [searchType, setSearchType] = useState("contains");
  const [searchValue, setSearchValue] = useState("");
  const [positionStart, setPositionStart] = useState("");
  const [positionEnd, setPositionEnd] = useState("");
  const [appliedAreaFilter, setAppliedAreaFilter] = useState({
    select_type: "",
    search_value_area: "",
    position_start: "",
    position_end: "",
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const uploadInputRef = useRef(null);
  const [selectedQRCodeEquipment, setSelectedQRCodeEquipment] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const qrRef = useRef();
  const flangeRequestControllerRef = useRef(null);
  const [ticketLoading, setTicketLoading] = useState(false);
  // Debounce search query
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [isFlangeModalOpen, setIsFlangeModalOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState({
    id: null,
    equipment_number: "",
  });
  const [flanges, setFlanges] = useState([]);
  const [flangesLoading, setFlangesLoading] = useState(false);
  const [flangesError, setFlangesError] = useState(null);
  const [showFlangeQRModal, setShowFlangeQRModal] = useState(false);
  const [selectedFlangeQR, setSelectedFlangeQR] = useState(null);
  const [showDrawingsListModal, setShowDrawingsListModal] = useState(false);
  const [selectedDrawingFiles, setSelectedDrawingFiles] = useState([]);
  const [drawingsListLoading, setDrawingsListLoading] = useState(false);
  const [drawingUploadModalOpen, setDrawingUploadModalOpen] = useState(false);
  const [drawingUploadEquipment, setDrawingUploadEquipment] = useState(null);
  const canManageDrawings = canManageEquipmentDrawings();
  // Drawings shown in the shared preview modal (full set + current index for
  // next/previous navigation).
  const [previewFiles, setPreviewFiles] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(null);
  const [drawingDeleteTarget, setDrawingDeleteTarget] = useState(null);
  const [drawingDeleteBusy, setDrawingDeleteBusy] = useState(false);
  const [drawingRenameTarget, setDrawingRenameTarget] = useState(null);
  const [drawingRenameValue, setDrawingRenameValue] = useState("");
  const [drawingRenameBusy, setDrawingRenameBusy] = useState(false);
  const [highlightedEquipmentId, setHighlightedEquipmentId] = useState(null);
  const [pulsedEquipmentId, setPulsedEquipmentId] = useState(null);
  const [activeStatusId, setActiveStatusId] = useState(null);
  const activeStatusIdRef = useRef(null);
  const [statusAnchorPosition, setStatusAnchorPosition] = useState(null);
  const [isStatusPinned, setIsStatusPinned] = useState(false);
  const highlightTimeoutRef = useRef(null);
  const pulseTimeoutRef = useRef(null);
  const statusCloseTimerRef = useRef(null);
  // Tracks which chip and whether the card itself is hovered
  const statusHoverStateRef = useRef({ chip: null, card: false });
  const statusPopoverContainer =
    typeof document !== "undefined" ? document.body : undefined;
  const processingScrollTargetRef = useRef(null);
  const redirectHighlightStateRef = useRef({
    key: "",
    notFoundShown: false,
  });
  const lastCompletedEquipmentFetchRef = useRef("");
  const skipNextSearchPageResetRef = useRef(false);
  const lastProjectRef = useRef("");
  const pendingGroupResolutionRef = useRef(false);
  const lastEffectFetchKeyRef = useRef("");

  const getEquipmentHighlightRequest = useCallback(
    (searchString = location.search) => {
      const searchParams = new URLSearchParams(searchString);
      const highlightEquipmentId = String(
        searchParams.get("highlightEquipmentId") ||
          searchParams.get("scrollTo") ||
          "",
      ).trim();
      const equipmentNumber = String(
        searchParams.get("equipmentNumber") || "",
      ).trim();
      const equipmentGroup = String(
        searchParams.get("equipmentGroup") || "",
      ).trim();
      const status = String(searchParams.get("status") || "")
        .trim()
        .toLowerCase();
      const holdStatus = String(searchParams.get("hold_status") || "")
        .trim()
        .toLowerCase();
      const fromDashboard = searchParams.get("source") === "dashboard";

      return {
        highlightEquipmentId,
        equipmentNumber,
        equipmentGroup,
        status,
        holdStatus,
        fromDashboard,
      };
    },
    [location.search],
  );

  const clearEquipmentHighlightQuery = useCallback(() => {
    const nextParams = new URLSearchParams(location.search);
    nextParams.delete("highlightEquipmentId");
    nextParams.delete("equipmentNumber");
    nextParams.delete("equipmentGroup");
    nextParams.delete("scrollTo");
    nextParams.delete("status");
    nextParams.delete("hold_status");
    nextParams.delete("source");
    const nextSearch = nextParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate]);

  const handleOpenImportPopup = () => {
    if (!isAdmin) return;
    setShowPopup(true);
    setSelectedFile(null);
  };

  const getDrawingLabel = (file) =>
    file?.display_name || file?.filename || "Unnamed file";

  // Keep ref and state in sync so closure-captured callbacks always read
  // the latest pinned value without needing to be re-created.
  const setStatusPinned = (val) => {
    isStatusPinnedRef.current = val;
    setIsStatusPinned(val);
  };

  // Keep activeStatusIdRef in sync with activeStatusId state so
  // closure-captured callbacks always read the latest value.
  const setActiveStatus = (val) => {
    activeStatusIdRef.current = val;
    setActiveStatusId(val);
  };

  // Lazily fetch the full drawing list for an equipment, then open the picker.
  const openAllDrawingsModal = async (equipment) => {
    setSelectedDrawingFiles([]);
    setShowDrawingsListModal(true);
    setDrawingsListLoading(true);
    try {
      const res = await axiosInstance.get(
        `${API_URL}${equipmentDrawingsListPath(equipment.id)}`,
      );
      setSelectedDrawingFiles(res?.data?.files || []);
    } catch (error) {
      toast.error("Unable to load drawings", { containerId: "main" });
      setSelectedDrawingFiles([]);
    } finally {
      setDrawingsListLoading(false);
    }
  };

  // Open the preview at a given file within a set (enables next/previous).
  const openDrawingPreviewAt = (files, idx) => {
    setPreviewFiles(files);
    setPreviewIndex(idx);
  };

  // Chip click: lazy-fetch the equipment's full drawing set, then open the
  // preview at the first (newest) one so the user can page through the rest.
  const openEquipmentDrawingPreview = async (equipment) => {
    try {
      const res = await axiosInstance.get(
        `${API_URL}${equipmentDrawingsListPath(equipment.id)}`,
      );
      const files = res?.data?.files || [];
      if (files.length === 0) {
        toast.warning("No drawing available for this equipment", {
          containerId: "main",
        });
        return;
      }
      openDrawingPreviewAt(files, 0);
    } catch (error) {
      toast.error("Unable to load drawing", { containerId: "main" });
    }
  };

  const closeDrawingPreview = () => {
    setPreviewIndex(null);
    setPreviewFiles([]);
  };

  // ---- Delete drawing (Admin or Planning Field Technician) ----
  const requestDeleteDrawing = (file) => setDrawingDeleteTarget(file);
  const cancelDeleteDrawing = () => {
    if (drawingDeleteBusy) return;
    setDrawingDeleteTarget(null);
  };

  const confirmDeleteDrawing = async () => {
    const target = drawingDeleteTarget;
    if (!target?.file_id) return;
    setDrawingDeleteBusy(true);
    try {
      await axiosInstance.delete(
        `${API_URL}${equipmentDrawingDeletePath(target.file_id)}`,
      );

      const remove = (arr) =>
        arr.filter((f) => f.file_id !== target.file_id);

      // Update the open picker list.
      setSelectedDrawingFiles((prev) => remove(prev));

      // Update the preview set + index (advance, or close if none left).
      setPreviewFiles((prev) => {
        const next = remove(prev);
        setPreviewIndex((i) => {
          if (i === null) return i;
          if (next.length === 0) return null;
          return Math.min(i, next.length - 1);
        });
        return next;
      });

      toast.success("Drawing deleted", { containerId: "main" });
      setDrawingDeleteTarget(null);

      // Refresh the list row so the chip / +N / count reflect the deletion.
      fetchEquipments(
        currentPage,
        itemsPerPage,
        debouncedSearchQuery,
        selectedEquipmentGroup?.code || "",
      );
    } catch (error) {
      const status = error?.response?.status;
      toast.error(
        status === 403
          ? "You do not have permission to delete drawings"
          : "Unable to delete drawing",
        { containerId: "main" },
      );
    } finally {
      setDrawingDeleteBusy(false);
    }
  };

  // ---- Rename drawing (friendly display name) ----
  const requestRenameDrawing = (file) => {
    setDrawingRenameTarget(file);
    setDrawingRenameValue(getDrawingLabel(file));
  };
  const cancelRenameDrawing = () => {
    if (drawingRenameBusy) return;
    setDrawingRenameTarget(null);
  };

  const confirmRenameDrawing = async () => {
    const target = drawingRenameTarget;
    const name = drawingRenameValue.trim();
    if (!target?.file_id || !name) return;
    setDrawingRenameBusy(true);
    try {
      await axiosInstance.patch(
        `${API_URL}${equipmentDrawingRenamePath(target.file_id)}`,
        { display_name: name },
      );

      const apply = (arr) =>
        arr.map((f) =>
          f.file_id === target.file_id ? { ...f, display_name: name } : f,
        );
      setSelectedDrawingFiles((prev) => apply(prev));
      setPreviewFiles((prev) => apply(prev));

      toast.success("Drawing renamed", { containerId: "main" });
      setDrawingRenameTarget(null);

      // Refresh so the list chip reflects the new name.
      fetchEquipments(
        currentPage,
        itemsPerPage,
        debouncedSearchQuery,
        selectedEquipmentGroup?.code || "",
      );
    } catch (error) {
      const status = error?.response?.status;
      toast.error(
        status === 403
          ? "You do not have permission to rename drawings"
          : "Unable to rename drawing",
        { containerId: "main" },
      );
    } finally {
      setDrawingRenameBusy(false);
    }
  };

  const openDrawingUploadModal = (equipment) => {
    setDrawingUploadEquipment(equipment);
    setDrawingUploadModalOpen(true);
  };

  const closeDrawingUploadModal = () => {
    setDrawingUploadModalOpen(false);
    setDrawingUploadEquipment(null);
  };

  const handleDrawingUploaded = () => {
    toast.success("Drawing uploaded successfully", { containerId: "main" });
    // Refresh the list in place so the new drawing status shows immediately.
    fetchEquipments(
      currentPage,
      itemsPerPage,
      debouncedSearchQuery,
      selectedEquipmentGroup?.code || "",
    );
  };

  // "View Drawing": one drawing opens in a new tab; multiple opens the picker.
  const raiseTicket = async () => {
    if (!selectedEquipmentId) {
      toast.error(
        "Unable to create the ticket because no equipment is selected.",
        EQUIPMENT_TOAST_OPTIONS,
      );
      return;
    }

    const ticketTitle = ticketTitleRef.current;
    const ticketDescription = ticketDescriptionRef.current;
    const validationErrors = {};
    if (!ticketTitle.trim()) validationErrors.title = "Enter a ticket title.";
    if (!ticketType) validationErrors.type = "Select a ticket type.";
    if (!ticketPriority) validationErrors.priority = "Select a priority.";

    if (Object.keys(validationErrors).length > 0) {
      setTicketErrors(validationErrors);
      toast.error(
        "Please correct the highlighted fields.",
        EQUIPMENT_TOAST_OPTIONS,
      );
      return;
    }

    setTicketErrors({});

    try {
      setTicketLoading(true);

      const formData = new FormData();
      formData.append("entity_type", "equipment");
      formData.append("equipment_id", selectedEquipmentId);
      formData.append("title", ticketTitle.trim());
      formData.append("description", ticketDescription);
      formData.append("ticket_type", ticketType);
      formData.append("priority", ticketPriority);
      ticketAssigneeUsers.forEach((user) =>
        formData.append("assignee_user_ids", user.id),
      );
      ticketAssigneeDepartments.forEach((dept) =>
        formData.append("assignee_department_ids", dept.id),
      );
      ticketAttachments.forEach((file) =>
        formData.append("attachments", file),
      );

      const res = await axiosInstance.post(`${API_URL}/api/ticket/v2`, formData);

      console.log("Ticket Response:", res.data);

      toast.success(
        res.data?.message || "Equipment ticket created successfully.",
        EQUIPMENT_TOAST_OPTIONS,
      );
      (res.data?.warnings || []).forEach((warning) => {
        if (warning?.message) {
          toast.warning(warning.message, EQUIPMENT_TOAST_OPTIONS);
        }
      });

      handleCloseTicketModal();
    } catch (error) {
      console.error("Raise Ticket Error:", error);
      toast.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Failed to create the equipment ticket. Please try again.",
        EQUIPMENT_TOAST_OPTIONS,
      );
    } finally {
      setTicketLoading(false);
    }
  };
  // Listen for project changes
  useEffect(() => {
    const handleProjectChange = (e) => {
      const nextProject =
        e.detail?.project_id ||
        JSON.parse(localStorage.getItem("selectedProject"));
      setSelectedProject((prevProject) => {
        const previousProjectId = String(prevProject?.id || prevProject || "");
        const nextProjectId = String(nextProject?.id || nextProject || "");

        return previousProjectId === nextProjectId ? prevProject : nextProject;
      });
    };

    window.addEventListener("projectChanged", handleProjectChange);

    return () => {
      window.removeEventListener("projectChanged", handleProjectChange);
    };
  }, []);
  // Fetch the project's assignable users/departments whenever the ticket
  // modal opens, so the assignment pickers reflect who can actually be
  // assigned on this project.
  useEffect(() => {
    if (!showTicketModal) return;
    const projectId = selectedProject?.id || selectedProject;
    if (!projectId) {
      setAssignableUsers([]);
      setAssignableDepartments([]);
      return;
    }

    let cancelled = false;
    setAssignablesLoading(true);
    axiosInstance
      .get(`${API_URL}/api/projects/${projectId}/assignable-users`)
      .then((response) => {
        if (cancelled) return;
        const depts = response.data?.data?.departments || [];
        setAssignableUsers(response.data?.data?.users || []);
        setAssignableDepartments(depts);
        // Pre-select the mandatory (planning) department(s): shown up front and
        // not removable.
        setTicketAssigneeDepartments((prev) =>
          withMandatoryDepartments(prev, depts),
        );
      })
      .catch(() => {
        if (cancelled) return;
        setAssignableUsers([]);
        setAssignableDepartments([]);
      })
      .finally(() => {
        if (!cancelled) setAssignablesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [showTicketModal, selectedProject]);

  useEffect(() => {
    return () => {
      clearStatusCloseTimer();
    };
  }, []);

  // Since the Popover backdrop has pointer-events:none (to prevent flicker),
  // MUI's onClose won't fire for outside clicks. This listener handles that.
  useEffect(() => {
    if (!activeStatusId) return;

    const handleDocumentClick = (e) => {
      // Don't close if the click is inside the popover card
      const popoverPaper = document.querySelector(
        ".equipment-status-popover-paper"
      );
      if (popoverPaper && popoverPaper.contains(e.target)) return;

      // Don't close if the click is on a status chip button
      const chipButton = e.target.closest(".equipment-delay-badge");
      if (chipButton) return;

      closeStatusCard();
    };

    // Use a microtask delay so the click that opened/pinned the card
    // doesn't immediately close it.
    const timerId = setTimeout(() => {
      document.addEventListener("click", handleDocumentClick, true);
    }, 0);

    return () => {
      clearTimeout(timerId);
      document.removeEventListener("click", handleDocumentClick, true);
    };
  }, [activeStatusId]);

  // Restore list state (filters/pagination) when returning from details.
  useEffect(() => {
    const currentProject =
      JSON.parse(localStorage.getItem("selectedProject")) || [];
    const {
      highlightEquipmentId,
      equipmentGroup: queryEquipmentGroup,
      status: queryStatus,
      holdStatus: queryHoldStatus,
      fromDashboard,
    } = getEquipmentHighlightRequest();

    setSelectedProject((prevProject) => {
      const previousProjectId = String(prevProject?.id || prevProject || "");
      const currentProjectId = String(
        currentProject?.id || currentProject || "",
      );

      return previousProjectId === currentProjectId
        ? prevProject
        : currentProject;
    });
    setError(null);

    try {
      const savedRaw = sessionStorage.getItem(EQUIPMENT_LIST_STATE_KEY);
      const saved = savedRaw ? JSON.parse(savedRaw) : null;
      const currentProjectId = currentProject?.id || currentProject;
      const canRestoreSavedState =
        !String(saved?.projectId || "") ||
        String(saved?.projectId || "") === String(currentProjectId || "");

      // Restore the persisted list state (page, group, area filter, status
      // filter) only ONCE, on the first run. This effect also re-runs whenever
      // `location.search` changes — notably when clearEquipmentHighlightQuery()
      // strips the ?status=/?highlightEquipmentId= params after highlighting.
      // Without this guard, that self-inflicted URL change would re-apply the
      // stale saved statusFilter and silently revert the filter the dashboard
      // (or the user) just selected — the "wrong filter" bug.
      if (!hasRestoredSavedStateRef.current) {
        hasRestoredSavedStateRef.current = true;

        if (
          canRestoreSavedState &&
          Number.isFinite(saved?.currentPage) &&
          saved.currentPage > 0
        ) {
          setCurrentPage(saved.currentPage);
        }
        if (
          canRestoreSavedState &&
          Number.isFinite(saved?.itemsPerPage) &&
          saved.itemsPerPage > 0
        ) {
          setItemsPerPage(saved.itemsPerPage);
        }
        if (
          canRestoreSavedState &&
          typeof saved?.selectedEquipmentGroupCode === "string" &&
          saved.selectedEquipmentGroupCode !== ""
        ) {
          setPendingGroupCode(saved.selectedEquipmentGroupCode);
          pendingGroupResolutionRef.current = true;
        }
        if (
          canRestoreSavedState &&
          saved?.appliedAreaFilter &&
          typeof saved.appliedAreaFilter === "object"
        ) {
          const restoredFilter = {
            select_type: saved.appliedAreaFilter.select_type || "",
            search_value_area: saved.appliedAreaFilter.search_value_area || "",
            position_start: saved.appliedAreaFilter.position_start || "",
            position_end: saved.appliedAreaFilter.position_end || "",
          };
          setAppliedAreaFilter(restoredFilter);
        }
        if (
          canRestoreSavedState &&
          STATUS_FILTERS.some((f) => f.value === saved?.statusFilter)
        ) {
          setStatusFilter(saved.statusFilter);
        }
        if (
          canRestoreSavedState &&
          HOLD_FILTERS.some((f) => f.value === saved?.holdStatusFilter)
        ) {
          setHoldStatusFilter(saved.holdStatusFilter);
        }
      }

      // Query params reflect explicit navigation intent (e.g. the dashboard
      // "Top Delayed" shortcut) and win over the restored saved state. These
      // run on every URL change, but become no-ops once the params are cleared.
      if (highlightEquipmentId) {
        setCurrentPage(1);
      }
      if (fromDashboard) {
        setSelectedEquipmentGroup(null);
        setPendingGroupCode("");
        pendingGroupResolutionRef.current = false;
        setAppliedAreaFilter({
          select_type: "",
          search_value_area: "",
          position_start: "",
          position_end: "",
        });
        setStatusFilter("all");
        setHoldStatusFilter("all");
        setCurrentPage(1);
      }
      if (queryEquipmentGroup) {
        setPendingGroupCode(queryEquipmentGroup);
        pendingGroupResolutionRef.current = true;
      }
      if (
        queryStatus &&
        STATUS_FILTERS.some((f) => f.value === queryStatus)
      ) {
        setStatusFilter(queryStatus);
        setCurrentPage(1);
      }
      if (
        queryHoldStatus &&
        HOLD_FILTERS.some((f) => f.value === queryHoldStatus)
      ) {
        setHoldStatusFilter(queryHoldStatus);
        setCurrentPage(1);
      }

      lastProjectRef.current = String(currentProjectId || "");
    } catch (restoreError) {
      console.error("Failed to restore equipment list state:", restoreError);
    }
  }, [getEquipmentHighlightRequest]);

  // If project changes, reset list filters/pagination and clear persisted equipment state.
  useEffect(() => {
    const currentProjectId = String(
      selectedProject?.id || selectedProject || "",
    );
    if (!currentProjectId) return;

    if (!lastProjectRef.current) {
      lastProjectRef.current = currentProjectId;
      return;
    }

    if (lastProjectRef.current !== currentProjectId) {
      sessionStorage.removeItem(EQUIPMENT_LIST_STATE_KEY);
      skipNextSearchPageResetRef.current = false;
      setSearchQuery("");
      setDebouncedSearchQuery("");
      setSelectedEquipmentGroup(null);
      setPendingGroupCode("");
      setAppliedAreaFilter({
        select_type: "",
        search_value_area: "",
        position_start: "",
        position_end: "",
      });
      setCurrentPage(1);
      setItemsPerPage(50);
      setStatusFilter("all");
      setHoldStatusFilter("all");
      lastProjectRef.current = currentProjectId;
    }
  }, [selectedProject]);

  // Debounce search query to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      if (skipNextSearchPageResetRef.current) {
        skipNextSearchPageResetRef.current = false;
      } else {
        setCurrentPage(1); // Reset to page 1 on user search changes
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (!pendingGroupCode || equipmentGroupOptions.length === 0) return;

    const matchedOption = equipmentGroupOptions.find(
      (option) => option.code === pendingGroupCode,
    );
    setSelectedEquipmentGroup(matchedOption || null);
    setPendingGroupCode("");
    pendingGroupResolutionRef.current = false;
  }, [pendingGroupCode, equipmentGroupOptions]);

  useEffect(() => {
    const projectId = selectedProject?.id || selectedProject || "";
    const snapshot = {
      projectId,
      currentPage,
      itemsPerPage,
      selectedEquipmentGroupCode: selectedEquipmentGroup?.code || "",
      appliedAreaFilter,
      statusFilter,
      holdStatusFilter,
    };
    sessionStorage.setItem(EQUIPMENT_LIST_STATE_KEY, JSON.stringify(snapshot));
  }, [
    selectedProject,
    currentPage,
    itemsPerPage,
    selectedEquipmentGroup,
    appliedAreaFilter,
    statusFilter,
    holdStatusFilter,
  ]);

  useEffect(() => {
    const fetchEquipmentGroups = async () => {
      try {
        const res = await axiosInstance.get(
          `${API_URL}/api/get_all_equipment_groups`,
        );
        const options = (res?.data?.data || []).map((item) => ({
          id: item.id,
          code: item.equipment_group_code || "",
          label: `${item.equipment_group_code || ""} - ${
            item.equipment_group_name || ""
          }`.trim(),
        }));
        setEquipmentGroupOptions(options);
      } catch (err) {
        console.error("Failed to fetch equipment groups:", err);
        setEquipmentGroupOptions([]);
      }
    };

    fetchEquipmentGroups();
  }, []);

  // Fetch equipments from API with useCallback to prevent recreation
  const fetchEquipments = useCallback(
    async (page = 1, limit = 50, search = "", equipmentCode = "") => {
      const projectParam = selectedProject?.id || selectedProject;
      if (!projectParam) {
        return;
      }

      const requestSignature = JSON.stringify({
        projectParam: String(projectParam),
        page,
        limit,
        search: String(search || "").trim(),
        equipmentCode: String(equipmentCode || "").trim(),
        status: statusFilter,
        holdStatus: holdStatusFilter,
        areaFilter: {
          select_type: appliedAreaFilter.select_type || "",
          search_value_area: appliedAreaFilter.search_value_area || "",
          position_start: appliedAreaFilter.position_start || "",
          position_end: appliedAreaFilter.position_end || "",
        },
      });

      setLoading(true);
      setError(null);

      try {
        let url = `${API_URL}/api/get_all_equipments/${projectParam}?page=${page}&per_page=${limit}`;

        if (search && search.trim() !== "") {
          url += `&search=${encodeURIComponent(search.trim())}`;
        }
        if (equipmentCode && equipmentCode.trim() !== "") {
          url += `&equipment_code=${encodeURIComponent(equipmentCode.trim())}`;
        }
        if (appliedAreaFilter.select_type) {
          url += `&select_type=${encodeURIComponent(appliedAreaFilter.select_type)}`;
        }
        if (appliedAreaFilter.search_value_area) {
          url += `&search_value_area=${encodeURIComponent(
            appliedAreaFilter.search_value_area,
          )}`;
        }
        if (appliedAreaFilter.position_start) {
          url += `&position_start=${encodeURIComponent(
            appliedAreaFilter.position_start,
          )}`;
        }
        if (appliedAreaFilter.position_end) {
          url += `&position_end=${encodeURIComponent(
            appliedAreaFilter.position_end,
          )}`;
        }
        if (statusFilter && statusFilter !== "all") {
          url += `&status=${encodeURIComponent(statusFilter)}`;
        }
        if (holdStatusFilter && holdStatusFilter !== "all") {
          url += `&hold_status=${encodeURIComponent(holdStatusFilter)}`;
        }

        const res = await axiosInstance.get(url);
        const data = res.data;
        const equipmentData = data.message || [];
        // Use total_records from backend response
        const totalCount = data.total_records || equipmentData.length;
        const nextStatusCounts =
          data.status_counts && typeof data.status_counts === "object"
            ? data.status_counts
            : { [statusFilter]: totalCount };
        setEquipments(equipmentData);
        setTotalRecords(totalCount);
        setStatusCounts(nextStatusCounts);
        setHoldCounts(
          data.hold_counts && typeof data.hold_counts === "object"
            ? data.hold_counts
            : {},
        );
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "Failed to fetch equipments";
        setError(errorMessage);
        console.error("Error fetching equipments:", err);
        setEquipments([]);
        setTotalRecords(0);
        setStatusCounts({});
        setHoldCounts({});
        showSnackbar(errorMessage, "error");
      } finally {
        lastCompletedEquipmentFetchRef.current = requestSignature;
        setTimeout(() => {
          if (isInitialLoad.current) {
            isInitialLoad.current = false;
          }
          setLoading(false);
        }, 1000);
      }
    },
    [selectedProject, appliedAreaFilter, statusFilter, holdStatusFilter],
  );

  // Fetch data when page, itemsPerPage, or search changes
  useEffect(() => {
    const projectParam = selectedProject?.id || selectedProject;
    if (!projectParam || pendingGroupResolutionRef.current) return;

    const requestKey = JSON.stringify({
      projectParam: String(projectParam),
      currentPage,
      itemsPerPage,
      search: (debouncedSearchQuery || "").trim(),
      equipmentCode: selectedEquipmentGroup?.code || "",
      status: statusFilter,
      holdStatus: holdStatusFilter,
      areaFilter: {
        select_type: appliedAreaFilter.select_type || "",
        search_value_area: appliedAreaFilter.search_value_area || "",
        position_start: appliedAreaFilter.position_start || "",
        position_end: appliedAreaFilter.position_end || "",
      },
    });

    if (lastEffectFetchKeyRef.current === requestKey) {
      return;
    }

    lastEffectFetchKeyRef.current = requestKey;

    fetchEquipments(
      currentPage,
      itemsPerPage,
      debouncedSearchQuery,
      selectedEquipmentGroup?.code || "",
    );
  }, [
    currentPage,
    itemsPerPage,
    debouncedSearchQuery,
    selectedEquipmentGroup,
    selectedProject,
    fetchEquipments,
    appliedAreaFilter,
    statusFilter,
    holdStatusFilter,
  ]);

  // Handle import equipment
  const handleImportEquipment = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;

    if (!selectedFile) {
      showSnackbar("Please select a file to import", "error");
      return;
    }

    const projectParam = selectedProject?.id || selectedProject;
    if (!projectParam) {
      showSnackbar("Project ID is required", "error");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("project_id", projectParam);

      const url = `${API_URL}/api/upload_equipment_excel/v2`;
      const res = await axiosInstance.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 300000,
      });

      const data = res.data;
      setImportResponse(data);

      if (data.requires_mapping) {
        setResourceMappingImport({
          importId: data.import_id,
          resources: cleanUnknownResources(data.unknown_resources),
          departments: data.departments || [],
        });
        setResourceMappingSelections({});
        setShowPopup(false);
        showSnackbar("Map each Excel resource to a department to continue", "info");
        return;
      }

      if (
        data.equipment_created !== undefined ||
        data.equipment_updated !== undefined
      ) {
        const createdCount = data.equipment_created || 0;
        const updatedCount = data.equipment_updated || 0;

        if (createdCount > 0 || updatedCount > 0) {
          showSnackbar(
            `Successfully imported: ${createdCount} created, ${updatedCount} updated`,
            "success",
          );
        } else {
          showSnackbar("No equipment was created or updated", "info");
        }
      }

      if (data.warnings && data.warnings.length > 0) {
        setTimeout(() => {
          showSnackbar(
            `${data.warnings.length} warning(s) occurred during import`,
            "warning",
          );
        }, 2000);
      }

      if (data.processes_created !== undefined) {
        setTimeout(() => {
          showSnackbar(`${data.processes_created} process(es) created`, "info");
        }, 3000);
      }

      // Refresh equipment list after import - reset to page 1
      setCurrentPage(1);
      setSearchQuery("");
      setDebouncedSearchQuery("");
      await fetchEquipments(
        1,
        itemsPerPage,
        "",
        selectedEquipmentGroup?.code || "",
      );
      handleCloseImportPopup();
    } catch (err) {
      console.error("Error importing equipment:", err);

      let errorMessage = "Failed to import equipment";

      if (err.code === "ECONNABORTED") {
        errorMessage = "Request timeout - file may be too large";
      } else if (err.response) {
        const responseMessage =
          err.response?.data?.message ||
          err.response?.data?.error ||
          errorMessage;
        errorMessage = err.response?.data?.help
          ? `${responseMessage} ${err.response.data.help}`
          : responseMessage;
      } else if (err.request) {
        errorMessage =
          "Network Error - Please check your connection and try again";
      } else {
        errorMessage = err.message || errorMessage;
      }

      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveResourceMappings = async () => {
    if (!resourceMappingImport || loading) return;
    const missing = resourceMappingImport.resources.filter(
      (resource) => !resourceMappingSelections[resource.normalized_source_name],
    );
    if (missing.length) {
      showSnackbar("Select a department for every resource", "warning");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const mappings = resourceMappingImport.resources.map((resource) => ({
        source_resource_name: resource.source_resource_name,
        department_id: resourceMappingSelections[resource.normalized_source_name],
      }));
      await axiosInstance.post(
        `${API_URL}/api/equipment_excel_imports/${resourceMappingImport.importId}/resource-mappings`,
        { mappings },
      );

      const resumeData = new FormData();
      resumeData.append("import_id", resourceMappingImport.importId);
      const res = await axiosInstance.post(
        `${API_URL}/api/upload_equipment_excel/v2`,
        resumeData,
        { timeout: 300000 },
      );
      const data = res.data;
      setImportResponse(data);

      if (data.requires_mapping) {
        setResourceMappingImport((current) => ({
          ...current,
          resources: cleanUnknownResources(data.unknown_resources),
          departments: data.departments || current.departments,
        }));
        setResourceMappingSelections({});
        showSnackbar("Additional resource mappings are required", "warning");
        return;
      }

      const createdCount = data.equipment_created || 0;
      const updatedCount = data.equipment_updated || 0;
      showSnackbar(
        createdCount || updatedCount
          ? `Successfully imported: ${createdCount} created, ${updatedCount} updated`
          : "Import completed with no equipment created or updated",
        createdCount || updatedCount ? "success" : "info",
      );
      setResourceMappingImport(null);
      setResourceMappingSelections({});
      setSelectedFile(null);
      setCurrentPage(1);
      setSearchQuery("");
      setDebouncedSearchQuery("");
      await fetchEquipments(
        1,
        itemsPerPage,
        "",
        selectedEquipmentGroup?.code || "",
      );
    } catch (err) {
      const responseMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        (err.code === "ECONNABORTED"
          ? "Import timed out. Please try resuming it again."
          : "Unable to save resource mappings and resume import");
      const errorMessage = err.response?.data?.help
        ? `${responseMessage} ${err.response.data.help}`
        : responseMessage;
      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  // Show snackbar notification
  const showSnackbar = (message, type = "info") => {
    setSnackbar({ show: true, message, type });
    setTimeout(() => {
      setSnackbar({ show: false, message: "", type: "" });
    }, 5000);
  };

  // Format date to readable string
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/A";
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "N/A";
    }
  };

  const formatDateParts = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;
      return {
        date: date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "2-digit",
        }),
        time: date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };
    } catch (error) {
      return null;
    }
  };

  const formatDelayHours = (value) => {
    if (value === null || value === undefined || value === "") return "N/A";

    const hours = Number(value);
    if (Number.isNaN(hours)) return "N/A";

    const sign = hours > 0 ? "+" : hours < 0 ? "-" : "";
    const absHours = Math.abs(hours);
    const formatNumber = (num) =>
      Number.isInteger(num) ? String(num) : String(Number(num.toFixed(2)));

    if (absHours < 24) {
      return `${sign}${formatNumber(absHours)}h`;
    }

    const days = Math.floor(absHours / 24);
    const remainingHours = absHours - days * 24;

    if (remainingHours === 0) {
      return `${sign}${days}d`;
    }

    return `${sign}${days}d ${formatNumber(remainingHours)}h`;
  };

  // Unsigned schedule-delay magnitude for the status chip (e.g. "8d 11h"). Shown
  // only for Delayed/Ahead — the chip word already conveys direction, so the +/-
  // sign is stripped. Returns null when there's nothing meaningful to display.
  const getChipDelayMagnitude = (equipment) => {
    const status = equipment.schedule_delay_status;
    if (status !== "Delayed" && status !== "Ahead") return null;
    const hours = equipment.schedule_delay_hours;
    if (
      hours === null ||
      hours === undefined ||
      hours === "" ||
      Number.isNaN(Number(hours))
    ) {
      return null;
    }
    // Round to whole hours so the chip stays compact (e.g. "8d 11h"); the exact
    // value is still shown in the hover popover.
    return formatDelayHours(Math.round(Number(hours))).replace(/^[+-]/, "");
  };

  // Badge for the equipment's schedule status. Shows the backend value verbatim —
  // never synthesizes "In Progress" (that derived label lied for "Not Started"
  // equipment). The colour tone is mapped; the label is whatever the API returned
  // (e.g. "Not Started", "No Plan", "Invalid Data").
  const getScheduleStatusBadge = (scheduleStatus) => {
    switch (scheduleStatus) {
      case "Ahead":
        return { label: "Ahead", tone: "process-ahead" };
      case "Delayed":
        return { label: "Delayed", tone: "process-delayed" };
      case "On Time":
        return { label: "On Time", tone: "process-on-time" };
      default:
        return { label: scheduleStatus || "N/A", tone: "default" };
    }
  };

  const getStatusTextClass = (status) => {
    switch (status) {
      case "Delayed":
        return "equipment-status-text-delayed";
      case "Ahead":
        return "equipment-status-text-ahead";
      case "On Time":
        return "equipment-status-text-on-time";
      default:
        return "equipment-status-text-default";
    }
  };

  // ─── Status popover hover logic ───────────────────────────────────────────
  //
  // Design goals:
  //   • Hovering a chip opens the card immediately (on first entry only —
  //     mouse movement *within* the chip between its child spans must not
  //     re-trigger open/close cycles).
  //   • Moving the cursor from the chip to the card must NOT close it —
  //     a 350 ms grace window lets the card stay open during travel.
  //   • Clicking a chip "pins" the card; it then only closes on an explicit
  //     second click on the same chip or when the user clicks outside.
  //   • isStatusPinnedRef mirrors isStatusPinned so every closure always
  //     reads the latest value without needing to be re-created.
  //
  // Root causes of the previous flicker:
  //   1. The chip button has child <span> elements.  Moving the cursor
  //      between them fires mouseleave → mouseenter on the button itself
  //      (child-crossing events bubble up), triggering a close-schedule
  //      then an immediate re-open on every micro-movement within the chip.
  //   2. openStatusPopup called setStatusAnchorPosition({...}) on every
  //      mouseenter even when the popup was already open for the same chip,
  //      giving MUI a new object reference → Popover re-render → flash.
  //   3. handleStatusChipMouseLeave scheduled a close even when the cursor
  //      was only moving to another child span still inside the button.

  const clearStatusCloseTimer = () => {
    if (statusCloseTimerRef.current) {
      clearTimeout(statusCloseTimerRef.current);
      statusCloseTimerRef.current = null;
    }
  };

  const closeStatusCard = () => {
    clearStatusCloseTimer();
    statusHoverStateRef.current = { chip: null, card: false };
    setStatusPinned(false);
    setActiveStatus(null);
    setStatusAnchorPosition(null);
  };

  // Grace period before actually closing.  If the cursor lands on the card
  // (handleStatusCardMouseEnter fires) within this window the timer is
  // cancelled and the card stays open.
  const scheduleStatusCardClose = () => {
    clearStatusCloseTimer();
    statusCloseTimerRef.current = setTimeout(() => {
      // Re-check both hover sources and the pinned flag before closing.
      if (statusHoverStateRef.current.chip || statusHoverStateRef.current.card) return;
      if (isStatusPinnedRef.current) return;
      setActiveStatus(null);
      setStatusAnchorPosition(null);
    }, 350); // 350 ms — enough to cover normal cursor travel between chip and card
  };

  // Called on mouseenter of a status chip.
  const openStatusPopup = (statusId, event) => {
    clearStatusCloseTimer();

    // If the card is already pinned, don't hijack it on hover.
    if (isStatusPinnedRef.current) return;

    // If this chip's popup is already open and positioned, do nothing further.
    // This prevents re-creating the anchorPosition object (and thus a MUI
    // Popover re-render) every time the cursor crosses a child <span> boundary
    // inside the button — which is the primary cause of the flicker on-chip.
    // We use the ref (not the state) so this guard is never defeated by stale
    // closure values during rapid child-crossing mouseenter events.
    if (activeStatusIdRef.current === statusId) {
      statusHoverStateRef.current.chip = statusId;
      return;
    }

    statusHoverStateRef.current.chip = statusId;
    const rect = event.currentTarget.getBoundingClientRect();
    setStatusAnchorPosition({
      top: Math.round(rect.bottom + window.scrollY + 8),
      left: Math.round(rect.left + window.scrollX),
    });
    setActiveStatus(statusId);
  };

  // Called on click of a status chip — toggles the pinned state.
  const toggleStatusPopup = (statusId, event) => {
    event.stopPropagation();
    clearStatusCloseTimer();

    const rect = event.currentTarget.getBoundingClientRect();
    const newPosition = {
      top: Math.round(rect.bottom + window.scrollY + 8),
      left: Math.round(rect.left + window.scrollX),
    };

    if (activeStatusId === statusId && isStatusPinnedRef.current) {
      // Second click on the same pinned chip → close.
      closeStatusCard();
      return;
    }

    // Pin the card for this chip.
    setStatusAnchorPosition(newPosition);
    setActiveStatus(statusId);
    setStatusPinned(true);
    statusHoverStateRef.current.chip = statusId;
  };

  const handleStatusChipMouseLeave = (statusId, event) => {
    // The button contains child <span> elements.  When the cursor moves
    // between them the browser fires mouseleave on the button with
    // relatedTarget pointing at the sibling span — still inside the button.
    // In that case we must NOT schedule a close; the chip is still hovered.
    const related = event?.relatedTarget;
    const currentTarget = event?.currentTarget;
    const isRelatedNode =
      typeof Node !== "undefined" && related instanceof Node;
    if (
      currentTarget &&
      typeof currentTarget.contains === "function" &&
      isRelatedNode &&
      currentTarget.contains(related)
    ) {
      return;
    }

    if (statusHoverStateRef.current.chip === statusId) {
      statusHoverStateRef.current.chip = null;
    }
    scheduleStatusCardClose();
  };

  const handleStatusCardMouseEnter = () => {
    // Cursor entered the card — cancel any pending close.
    statusHoverStateRef.current.card = true;
    clearStatusCloseTimer();
  };

  const handleStatusCardMouseLeave = () => {
    statusHoverStateRef.current.card = false;
    // Only schedule close when not pinned.
    if (!isStatusPinnedRef.current) {
      scheduleStatusCardClose();
    }
  };
  // ─────────────────────────────────────────────────────────────────────────

  // Check if any equipment has QR codes
  const hasAnyQRCode = () => {
    return equipments.some(
      (equipment) => equipment.qr_code && equipment.qr_code !== null,
    );
  };

  // Handle QR code view
  const handleViewQRCode = (equipment) => {
    setSelectedQRCodeEquipment(equipment);
    setShowQRModal(true);
  };

  // Handle QR code download
  const handleDownloadQRCode = async () => {
    try {
      if (!selectedQRCodeEquipment || !selectedQRCodeEquipment.id) {
        showSnackbar("Equipment ID not found", "error");
        return;
      }

      const response = await axiosInstance.post(
        `${API_URL}/api/download_qr_code/${selectedQRCodeEquipment.id}`,
      );

      if (response.data.message) {
        const link = document.createElement("a");
        link.href = `data:image/png;base64,${selectedQRCodeEquipment.qr_code}`;
        link.download = `qr_code_${
          selectedQRCodeEquipment.equipment_tag_number ||
          selectedQRCodeEquipment.id
        }.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        showSnackbar("QR code downloaded successfully", "success");
      }
    } catch (error) {
      console.error("Error downloading QR code:", error);
      showSnackbar("Failed to download QR code", "error");
    }
  };

  // Resolve a human-readable project/company name from the selected project
  const getProjectDisplayName = () => {
    const p = selectedProject;
    if (!p) return "";
    if (typeof p === "string") return p;
    if (Array.isArray(p)) return "";
    return p.project_name || p.projectName || p.name || p.title || p.label || "";
  };

  // Escape user-supplied values before injecting them into the print document
  const escapeHtml = (value) =>
    String(value ?? "").replace(
      /[&<>"']/g,
      (ch) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[ch],
    );

  // Handle QR code print
  const handlePrintQRCode = (equipment) => {
    if (!equipment?.qr_code) return;

    const equipmentNo = escapeHtml(equipment.equipment_tag_number || "N/A");
    const projectName = escapeHtml(getProjectDisplayName());

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Print QR</title>
          <style>
            @page { margin: 12mm; }
            body {
              font-family: Arial, Helvetica, sans-serif;
              text-align: center;
              color: #111827;
              margin: 0;
              padding: 24px;
            }
            .qr-print-project {
              font-size: 16px;
              font-weight: 600;
              margin-bottom: 16px;
            }
            .qr-print-image {
              width: 240px;
              height: 240px;
              object-fit: contain;
            }
            .qr-print-label {
              margin-top: 16px;
              font-size: 20px;
              font-weight: 700;
            }
          </style>
        </head>
        <body>
          ${projectName ? `<div class="qr-print-project">${projectName}</div>` : ""}
          <img class="qr-print-image" src="data:image/png;base64,${equipment.qr_code}" alt="QR Code for ${equipmentNo}" />
          <div class="qr-print-label">Equipment No: ${equipmentNo}</div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const fetchAssociatedFlanges = useCallback(async (equipmentId) => {
    if (!equipmentId) return;

    if (flangeRequestControllerRef.current) {
      flangeRequestControllerRef.current.abort();
    }

    const controller = new AbortController();
    flangeRequestControllerRef.current = controller;

    setFlangesLoading(true);
    setFlangesError(null);

    try {
      const res = await axiosInstance.get(
        `${API_URL}/api/all_flange_of_equipment/${equipmentId}`,
        { signal: controller.signal },
      );
      setFlanges(res.data?.flanges || []);
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
        return;
      }
      setFlangesError(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch associated flanges.",
      );
      setFlanges([]);
    } finally {
      setFlangesLoading(false);
    }
  }, []);

  const handleOpenFlangeModal = (equipment) => {
    const sameEquipment = selectedEquipment?.id === equipment.id;
    if (flangesLoading && sameEquipment && isFlangeModalOpen) return;

    setSelectedEquipment({
      id: equipment.id,
      equipment_number: equipment.equipment_tag_number || "N/A",
    });
    setIsFlangeModalOpen(true);
    setFlanges([]);
    setFlangesError(null);
    fetchAssociatedFlanges(equipment.id);
  };

  const handleCloseFlangeModal = () => {
    setIsFlangeModalOpen(false);
    handleCloseFlangeQRModal();
  };

  useEffect(() => {
    return () => {
      if (flangeRequestControllerRef.current) {
        flangeRequestControllerRef.current.abort();
      }
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
      if (pulseTimeoutRef.current) {
        clearTimeout(pulseTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const {
      highlightEquipmentId,
      equipmentNumber,
      equipmentGroup,
    } = getEquipmentHighlightRequest(location.search);
    const requestKey = highlightEquipmentId
      ? `${highlightEquipmentId}::${equipmentNumber}::${equipmentGroup}`
      : "";

    if (!requestKey) {
      redirectHighlightStateRef.current = {
        key: "",
        notFoundShown: false,
      };
      return;
    }

    if (redirectHighlightStateRef.current.key !== requestKey) {
      redirectHighlightStateRef.current = {
        key: requestKey,
        notFoundShown: false,
      };
      processingScrollTargetRef.current = null;
    }

    if (!equipmentNumber) {
      return;
    }

    const normalizedSearch = String(searchQuery || "").trim();
    const normalizedDebouncedSearch = String(
      debouncedSearchQuery || "",
    ).trim();
    const normalizedTargetSearch = String(equipmentNumber || "").trim();

    if (
      normalizedTargetSearch &&
      (normalizedSearch !== normalizedTargetSearch ||
        normalizedDebouncedSearch !== normalizedTargetSearch ||
        currentPage !== 1)
    ) {
      setCurrentPage(1);
      if (normalizedSearch !== normalizedTargetSearch) {
        setSearchQuery(normalizedTargetSearch);
      }
      if (normalizedDebouncedSearch !== normalizedTargetSearch) {
        setDebouncedSearchQuery(normalizedTargetSearch);
      }
    }
  }, [
    currentPage,
    debouncedSearchQuery,
    getEquipmentHighlightRequest,
    holdStatusFilter,
    location.search,
    searchQuery,
  ]);

  useEffect(() => {
    const {
      highlightEquipmentId,
      equipmentNumber,
      equipmentGroup,
    } = getEquipmentHighlightRequest(location.search);

    if (!highlightEquipmentId || loading) {
      return;
    }

    const activeRedirectState = redirectHighlightStateRef.current;
    const requestKey = `${highlightEquipmentId}::${equipmentNumber}::${equipmentGroup}`;
    if (activeRedirectState.key !== requestKey) {
      return;
    }

    const projectParam = selectedProject?.id || selectedProject;
    if (!projectParam || pendingGroupResolutionRef.current) {
      return;
    }

    const normalizedTargetSearch = String(equipmentNumber || "").trim();
    const normalizedDebouncedSearch = String(
      debouncedSearchQuery || "",
    ).trim();

    const expectedFetchSignature = JSON.stringify({
      projectParam: String(projectParam),
      page: currentPage,
      limit: itemsPerPage,
      search: normalizedDebouncedSearch,
      equipmentCode: selectedEquipmentGroup?.code || "",
      status: statusFilter,
      holdStatus: holdStatusFilter,
      areaFilter: {
        select_type: appliedAreaFilter.select_type || "",
        search_value_area: appliedAreaFilter.search_value_area || "",
        position_start: appliedAreaFilter.position_start || "",
        position_end: appliedAreaFilter.position_end || "",
      },
    });

    if (normalizedTargetSearch) {
      if (currentPage !== 1) {
        return;
      }
      if (normalizedDebouncedSearch !== normalizedTargetSearch) {
        return;
      }
    }

    if (lastCompletedEquipmentFetchRef.current !== expectedFetchSignature) {
      return;
    }

    if (processingScrollTargetRef.current === highlightEquipmentId) {
      return;
    }

    const matchedById = equipments.find((equipment) => {
      const rowEquipmentId = String(
        equipment?.equipment_id ?? equipment?.id ?? "",
      ).trim();
      return rowEquipmentId && rowEquipmentId === highlightEquipmentId;
    });

    const matchedEquipment =
      matchedById ||
      equipments.find((equipment) => {
        const rowEquipmentNumber = String(
          equipment?.equipment_tag_number ??
            equipment?.equipment_number ??
            equipment?.tag_number ??
            "",
        ).trim();
        return Boolean(
          equipmentNumber &&
            rowEquipmentNumber &&
            rowEquipmentNumber === equipmentNumber,
        );
      });

    const targetEquipmentId = String(
      matchedEquipment?.equipment_id ?? matchedEquipment?.id ?? "",
    );

    const finishWithNotFound = () => {
      if (redirectHighlightStateRef.current.notFoundShown) {
        return;
      }
      redirectHighlightStateRef.current.notFoundShown = true;
      showSnackbar(
        "Requested equipment could not be found. Try clearing filters or refreshing the list.",
        "info",
      );
      clearEquipmentHighlightQuery();
      processingScrollTargetRef.current = null;
    };

    if (!targetEquipmentId) {
      finishWithNotFound();
      return;
    }

    processingScrollTargetRef.current = highlightEquipmentId;

    const timer = setTimeout(() => {
      const targetRow = document.getElementById(
        `equipment-row-${targetEquipmentId}`,
      );
      if (!targetRow) {
        finishWithNotFound();
        return;
      }

      targetRow.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedEquipmentId(targetEquipmentId);
      setPulsedEquipmentId(targetEquipmentId);

      if (pulseTimeoutRef.current) {
        clearTimeout(pulseTimeoutRef.current);
      }
      pulseTimeoutRef.current = setTimeout(() => {
        setPulsedEquipmentId(null);
      }, 700);

      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
      highlightTimeoutRef.current = setTimeout(() => {
        setHighlightedEquipmentId(null);
      }, 2600);

      clearEquipmentHighlightQuery();
      processingScrollTargetRef.current = null;
    }, 120);

    return () => clearTimeout(timer);
  }, [
    appliedAreaFilter,
    clearEquipmentHighlightQuery,
    currentPage,
    debouncedSearchQuery,
    equipments,
    getEquipmentHighlightRequest,
    holdStatusFilter,
    itemsPerPage,
    loading,
    location.search,
    selectedEquipmentGroup,
    selectedProject,
    statusFilter,
  ]);

  const getFlangeStatusClass = (status) => {
    const normalized = String(status || "").toLowerCase();
    if (normalized.includes("progress")) return "flange-status-progress";
    if (normalized.includes("complete")) return "flange-status-completed";
    if (normalized.includes("block")) return "flange-status-blocked";
    return "flange-status-default";
  };

  const getQrImageSrc = (qrCode) => {
    if (!qrCode) return "";
    if (String(qrCode).startsWith("data:image")) return qrCode;
    return `data:image/png;base64,${qrCode}`;
  };

  const hasValidQrCode = (qrCode) => {
    if (!qrCode) return false;
    const qr = String(qrCode).trim();
    if (!qr) return false;
    if (qr.startsWith("data:image")) return true;
    try {
      window.atob(qr);
      return true;
    } catch {
      return false;
    }
  };

  const handleOpenFlangeQRModal = (flange) => {
    if (!hasValidQrCode(flange?.qr_code)) return;
    setSelectedFlangeQR({
      qr_code: flange.qr_code,
      flange_number: flange.flange_number || "",
    });
    setShowFlangeQRModal(true);
  };

  const handlePrintFlangeQRCode = () => {
    if (!selectedFlangeQR?.qr_code) return;
    const printWindow = window.open("", "_blank");
    const qrSrc = getQrImageSrc(selectedFlangeQR.qr_code);
    const flangeNumber = selectedFlangeQR.flange_number || "N/A";

    printWindow.document.write(`
      <html>
        <head>
          <title>Print QR</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 24px;
              text-align: center;
            }
            h2 { margin: 0 0 10px 0; }
            p { margin: 0 0 18px 0; color: #333; }
            img { width: 300px; height: 300px; object-fit: contain; }
          </style>
        </head>
        <body>
          <h2>QR Code</h2>
          <p>Flange Number: ${flangeNumber}</p>
          <img src="${qrSrc}" alt="Flange QR Code" />
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const handleCloseFlangeQRModal = () => {
    setShowFlangeQRModal(false);
    setSelectedFlangeQR(null);
  };

  const handleOpenFlangeInAllFlanges = (flange) => {
    const flangeId = flange?.flange_id || flange?.id;
    if (!flangeId) return;

    const params = new URLSearchParams({
      scrollTo: String(flangeId),
    });
    const flangeNumber = String(flange?.flange_number || "").trim();
    if (flangeNumber) {
      params.set("flangeNumber", flangeNumber);
    }

    handleCloseFlangeModal();
    navigate(`/flanges?${params.toString()}`);
  };

  const handleScheduleExport = async () => {
    if (!isAdmin || scheduleExporting) return;

    const projectId = String(
      selectedProject?.id ||
        (typeof selectedProject === "string" ? selectedProject : ""),
    ).trim();
    if (!projectId) {
      showSnackbar("Select a project before exporting the schedule", "warning");
      return;
    }

    setScheduleExporting(true);
    try {
      const response = await axiosInstance.get(
        `${API_URL}/api/equipment/schedule-export/${encodeURIComponent(projectId)}`,
        { responseType: "blob" },
      );
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const contentDisposition = response.headers?.["content-disposition"] || "";
      const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filenameMatch?.[1] || "equipment_schedule.xlsx";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
      showSnackbar("Equipment schedule exported successfully", "success");
    } catch (exportError) {
      console.error("Equipment schedule export failed:", exportError);
      let message = "Failed to export equipment schedule";
      if (exportError?.response?.data instanceof Blob) {
        try {
          const payload = JSON.parse(await exportError.response.data.text());
          message = payload?.message || message;
        } catch {}
      }
      showSnackbar(message, "error");
    } finally {
      setScheduleExporting(false);
    }
  };

  const handleCloseImportPopup = () => {
    setShowPopup(false);
    setSelectedFile(null);
    setImportResponse(null);
    if (uploadInputRef.current) uploadInputRef.current.value = "";
  };

  const handleOpenAreaPopup = () => {
    setSearchType(appliedAreaFilter.select_type || "contains");
    setSearchValue(appliedAreaFilter.search_value_area || "");
    setPositionStart(appliedAreaFilter.position_start || "");
    setPositionEnd(appliedAreaFilter.position_end || "");
    setShowAreaPopup(true);
  };

  const handleCloseAreaPopup = () => {
    setShowAreaPopup(false);
  };

  const isPositiveIntegerString = (value) => /^[1-9]\d*$/.test(String(value));
  const sanitizePositiveInteger = (value) => String(value || "").replace(/\D/g, "");

  const handlePositionStartChange = (value) => {
    setPositionStart(sanitizePositiveInteger(value));
  };

  const handlePositionEndChange = (value) => {
    setPositionEnd(sanitizePositiveInteger(value));
  };

  const positionStartNum = Number(positionStart);
  const positionEndNum = Number(positionEnd);
  const positionStartInvalid = positionStart !== "" && positionStartNum <= 0;
  const positionEndInvalid = positionEnd !== "" && positionEndNum <= 0;
  const areaRangeInvalid =
    positionStart !== "" &&
    positionEnd !== "" &&
    positionEndNum < positionStartNum;
  const isAreaApplyDisabled =
    !searchValue.trim() ||
    positionStartInvalid ||
    positionEndInvalid ||
    areaRangeInvalid;

  const handleAreaFilterApply = () => {
    const trimmedSearchValue = searchValue.trim();
    const trimmedPositionStart = String(positionStart || "").trim();
    const trimmedPositionEnd = String(positionEnd || "").trim();

    if (!trimmedSearchValue) {
      showSnackbar("Search Value is required", "warning");
      return;
    }

    if (
      trimmedPositionStart &&
      !isPositiveIntegerString(trimmedPositionStart)
    ) {
      showSnackbar("Position Start must be a valid positive number", "warning");
      return;
    }

    if (trimmedPositionEnd && !isPositiveIntegerString(trimmedPositionEnd)) {
      showSnackbar("Position End must be a valid positive number", "warning");
      return;
    }

    if (
      trimmedPositionStart &&
      trimmedPositionEnd &&
      Number(trimmedPositionEnd) < Number(trimmedPositionStart)
    ) {
      showSnackbar(
        "Position End must be greater than or equal to Position Start",
        "warning",
      );
      return;
    }

    setAppliedAreaFilter({
      select_type: searchType,
      search_value_area: trimmedSearchValue,
      position_start: trimmedPositionStart,
      position_end: trimmedPositionEnd,
    });
    setCurrentPage(1);
    setShowAreaPopup(false);
  };

  const clearAreaFilter = () => {
    setAppliedAreaFilter({
      select_type: "",
      search_value_area: "",
      position_start: "",
      position_end: "",
    });
    setSearchType("contains");
    setSearchValue("");
    setPositionStart("");
    setPositionEnd("");
    setCurrentPage(1);
    setShowAreaPopup(false);
  };

  const isAreaFilterActive = Boolean(appliedAreaFilter.search_value_area);

  const activeStatusEquipment =
    activeStatusId == null
      ? null
      : equipments.find(
          (equipment) =>
            String(equipment?.equipment_id ?? equipment?.id ?? "") ===
            String(activeStatusId),
        ) || null;

  return (
    <>
      <div>
        <div
          className={`table-container equipment-container equipment-page ${
            isSidebarExpanded ? "expanded" : "collapsed"
          }`}
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div className="equipment-page-content">
          <div className="equipment-page-header mt-2">
            <div className="equipment-header-titleblock">
              <h2 className="page-title mb-0">Equipments</h2>
            </div>
            <div className="equipment-header-buttons d-flex align-items-center">
              <div className="equipment-header-controls-row">
                <div className="equipment-search-inline">
                  <SearchBar
                    placeholder="Search by Equipment Number, Equipment System No..."
                    value={searchQuery}
                    onResults={({ query }) => {
                      setSearchQuery(query);
                      setCurrentPage(1);
                    }}
                  />
                </div>
                <div className="equipment-group-dropdown-inline">
                  <FormControl
                    fullWidth
                    size="small"
                    className="equipment-group-form-control"
                  >
                    <Select
                      value={selectedEquipmentGroup?.code || ""}
                      displayEmpty
                      onChange={(e) => {
                        const selectedCode = e.target.value;
                        const selectedOption = equipmentGroupOptions.find(
                          (option) => option.code === selectedCode,
                        );
                        setSelectedEquipmentGroup(selectedOption || null);
                        setCurrentPage(1);
                      }}
                      input={
                        <OutlinedInput
                          endAdornment={
                            selectedEquipmentGroup ? (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation(); // prevents dropdown from opening
                                    setSelectedEquipmentGroup(null);
                                    setCurrentPage(1);
                                  }}
                                >
                                  <ClearIcon
                                    sx={{
                                      width: 16,
                                      height: 16,
                                      fontSize: 16,
                                      marginRight: "5px",
                                    }}
                                  />
                                </IconButton>
                              </InputAdornment>
                            ) : null
                          }
                        />
                      }
                      renderValue={(selected) => {
                        if (!selected) {
                          return (
                            <span style={{ color: "#aaa" }}>
                              Select Equipment Group
                            </span>
                          );
                        }
                        return (
                          equipmentGroupOptions.find(
                            (option) => option.code === selected,
                          )?.label || selected
                        );
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 240,
                            overflowY: "auto",
                            zIndex: 1400,
                          },
                        },
                      }}
                    >
                      {equipmentGroupOptions.map((option) => (
                        <MenuItem key={option.id} value={option.code}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                {isAdmin && (
                  <button
                    className="import-button equipment-import-button area-filter-trigger-btn"
                    onClick={handleOpenImportPopup}
                  >
                    <TbFileImport /> <span>Import</span>
                  </button>
                )}
                {isAdmin && (
                  <button
                    type="button"
                    className="import-button equipment-import-button area-filter-trigger-btn"
                    onClick={handleScheduleExport}
                    disabled={
                      scheduleExporting ||
                      !(
                        selectedProject?.id ||
                        (typeof selectedProject === "string" && selectedProject)
                      )
                    }
                    title="Export the project equipment schedule to Excel"
                  >
                    {scheduleExporting ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <TbFileExport />
                    )}
                    <span>{scheduleExporting ? "Exporting..." : "Export Schedule"}</span>
                  </button>
                )}
                <button
                  className="import-button equipment-import-button area-filter-trigger-btn"
                  onClick={handleOpenAreaPopup}
                >
                  <FiFilter size={16} />
                  <span>
                    {isAreaFilterActive ? "Area Filter Applied" : "Area Filter"}
                  </span>
                </button>
                {isAreaFilterActive && (
                  <button
                    className="import-button equipment-import-button area-filter-clear-btn"
                    onClick={clearAreaFilter}
                  >
                    <IoIosClose size={16} />
                    Clear Area Filter
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="equipment-status-filters">
            {STATUS_FILTERS.map((f) => {
              const count = statusCounts[f.value];
              return (
                <button
                  key={f.value}
                  type="button"
                  className={`equipment-status-filter-chip${
                    statusFilter === f.value ? " active" : ""
                  }`}
                  onClick={() => {
                    setStatusFilter(f.value);
                    setCurrentPage(1);
                  }}
                >
                  <span>{f.label}</span>
                  {count != null && (
                    <span className="equipment-status-filter-count">
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
            <span className="equipment-filter-divider" aria-hidden="true" />
            {HOLD_FILTERS.map((filter) => {
              const count = holdCounts[filter.value];
              return (
                <button
                  key={filter.value}
                  type="button"
                  className={`equipment-status-filter-chip equipment-hold-filter-chip${
                    holdStatusFilter === filter.value ? " active" : ""
                  }`}
                  onClick={() => {
                    setHoldStatusFilter(filter.value);
                    setCurrentPage(1);
                  }}
                >
                  <span>{filter.label}</span>
                  {count != null && (
                    <span className="equipment-status-filter-count">{count}</span>
                  )}
                </button>
              );
            })}
          </div>

          {error && !resourceMappingImport && (
            <div className="equipment-error-message">
              <strong>Error:</strong> {error}
              <button
                onClick={() => {
                  setError(null);
                  fetchEquipments(
                    currentPage,
                    itemsPerPage,
                    debouncedSearchQuery,
                    selectedEquipmentGroup?.code || "",
                  );
                }}
                style={{
                  marginLeft: "10px",
                  padding: "5px 10px",
                  cursor: "pointer",
                }}
              >
                Retry
              </button>
            </div>
          )}

          <div className="equipment-table-card">
            <div
              className="equipment-table-wrapper"
              style={{ flex: 1, minHeight: 0 }}
            >
              <table className="equipment-data-table">
                <colgroup>
                  <col className="equipment-col-sr" />
                  <col className="equipment-col-group" />
                  <col className="equipment-col-type" />
                  <col className="equipment-col-equip-no" />
                  <col className="equipment-col-duration" />
                  <col className="equipment-col-eta-range" />
                  <col className="equipment-col-progress-plan" />
                  <col className="equipment-col-status" />
                  <col className="equipment-col-drawings" />
                  <col className="equipment-col-actions" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Sr. No</th>
                    <th title="Equipment Group">Group</th>
                    <th>Equipment Type</th>
                    <th>Equip No.</th>
                    <th title="Total Duration">Duration</th>
                    <th title="Start ETA → Finish ETA">Start → Finish ETA</th>
                    <th title="Actual progress vs planned progress (by today)">
                      Progress vs Plan
                    </th>
                    <th
                      className="equipment-status-col"
                      title="Status vs project schedule"
                    >
                      Status
                    </th>
                    <th>Drawings</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 10 }).map((_, rowIdx) => (
                      <tr
                        key={`equipment-skeleton-${rowIdx}`}
                        className="equipment-skeleton-row"
                      >
                        {Array.from({ length: 10 }).map((__, colIdx) => (
                          <td key={colIdx}>
                            <span className="equipment-skeleton-bar" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : equipments.length > 0 ? (
                    equipments.map((equipment, index) => {
                      const serialNumber =
                        (currentPage - 1) * itemsPerPage + index + 1;
                      const rowEquipmentId = String(
                        equipment?.equipment_id ?? equipment?.id ?? "",
                      );
                      const isScrollHighlight =
                        rowEquipmentId &&
                        highlightedEquipmentId === rowEquipmentId;
                      const isScrollPulse =
                        rowEquipmentId && pulsedEquipmentId === rowEquipmentId;

                      return (
                        <tr
                          key={rowEquipmentId || index}
                          id={
                            rowEquipmentId
                              ? `equipment-row-${rowEquipmentId}`
                              : undefined
                          }
                          data-equipment-id={rowEquipmentId || undefined}
                          className={`${isScrollHighlight ? "equipment-scroll-highlight" : ""} ${
                            isScrollPulse ? "equipment-scroll-pulse" : ""
                          }`.trim()}
                        >
                          <td>{serialNumber}</td>
                          <td>{equipment.equipment_group || "N/A"}</td>
                          <td
                            className="truncate-cell"
                            title={equipment.equipment_type || "N/A"}
                          >
                            {equipment.equipment_type || "N/A"}
                          </td>
                          <td>
                            <div className="equipment-tag-cell">
                              {equipment.is_on_hold && (
                                <Tooltip
                                  arrow
                                  placement="bottom-start"
                                  enterDelay={100}
                                  leaveDelay={100}
                                  slotProps={{
                                    tooltip: {
                                      className: "equipment-hold-tooltip-paper",
                                    },
                                    arrow: {
                                      className: "equipment-hold-tooltip-arrow",
                                    },
                                  }}
                                  title={
                                    <div className="equipment-hold-tooltip">
                                      <div className="equipment-hold-tooltip-title">
                                        <HoldIcon aria-hidden="true" />
                                        <strong>Equipment On Hold</strong>
                                      </div>
                                      <div className="equipment-hold-tooltip-row">
                                        <span className="equipment-hold-tooltip-label">
                                          Reason:
                                        </span>
                                        <span className="equipment-hold-tooltip-value">
                                          {equipment.hold_reason || "Not provided"}
                                        </span>
                                      </div>
                                      <div className="equipment-hold-tooltip-row">
                                        <span className="equipment-hold-tooltip-label">
                                          Held by:
                                        </span>
                                        <span className="equipment-hold-tooltip-value">
                                          {equipment.held_by?.name || "Unknown user"}
                                        </span>
                                      </div>
                                      <div className="equipment-hold-tooltip-row">
                                        <span className="equipment-hold-tooltip-label">
                                          Held at:
                                        </span>
                                        <span className="equipment-hold-tooltip-value">
                                          {formatHoldTimestamp(equipment.held_at)}
                                        </span>
                                      </div>
                                    </div>
                                  }
                                >
                                  <span
                                    className="equipment-hold-indicator"
                                    role="img"
                                    aria-label="Equipment on hold"
                                    tabIndex={0}
                                  >
                                    <HoldIcon aria-hidden="true" />
                                  </span>
                                </Tooltip>
                              )}

                              <Tooltip
                                arrow
                                title={`System No: ${
                                  equipment.system_number_for_this_equipment ||
                                  "N/A"
                                }`}
                              >
                                <span
                                  className="equipment-tag-link"
                                  role="button"
                                  tabIndex={0}
                                  onClick={() =>
                                    navigate(
                                      `/equipment-details/${equipment.id}`,
                                      { state: { from: "equipment-list" } },
                                    )
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      navigate(
                                        `/equipment-details/${equipment.id}`,
                                        { state: { from: "equipment-list" } },
                                      );
                                    }
                                  }}
                                >
                                  {equipment.equipment_tag_number || "N/A"}
                                </span>
                              </Tooltip>

                            </div>
                          </td>
                          <td>
                            {equipment.total_duration_eta
                              ? `${equipment.total_duration_eta} hrs`
                              : "N/A"}
                          </td>
                          <td className="eta-range-cell">
                            {(() => {
                              const startEta = formatDateParts(
                                equipment.start_eta,
                              );
                              const finishEta = formatDateParts(
                                equipment.finish_eta,
                              );
                              return (
                                <div className="eta-range">
                                  <div className="eta-range-line">
                                    {startEta ? (
                                      <>
                                        <span className="eta-range-date">
                                          {startEta.date}
                                        </span>
                                        <span className="eta-range-time">
                                          {startEta.time}
                                        </span>
                                      </>
                                    ) : (
                                      <span className="eta-range-date">N/A</span>
                                    )}
                                  </div>
                                  <div className="eta-range-line eta-range-line-finish">
                                    <span
                                      className="eta-range-arrow"
                                      aria-hidden="true"
                                    >
                                      ›
                                    </span>
                                    {finishEta ? (
                                      <>
                                        <span className="eta-range-date">
                                          {finishEta.date}
                                        </span>
                                        <span className="eta-range-time">
                                          {finishEta.time}
                                        </span>
                                      </>
                                    ) : (
                                      <span className="eta-range-date">N/A</span>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}
                          </td>
                          <td className="progress-plan-cell">
                            {(() => {
                              const completed =
                                equipment.completed_process_count ?? 0;
                              const total = equipment.process_count ?? 0;
                              const pct =
                                total > 0
                                  ? Math.round((completed / total) * 100)
                                  : 0;
                              const actual = Number(
                                equipment.actual_percent ?? 0,
                              );
                              const planned = Number(
                                equipment.planned_percent ?? 0,
                              );
                              const diff = actual - planned;
                              // Existing plan-vs-actual tone logic, rendered only
                              // as the bar colour (the bulky pill is gone). Green
                              // is reserved for complete/ahead, red for behind,
                              // neutral blue for normal progress.
                              let tone = "normal";
                              if (pct >= 100) tone = "complete";
                              else if (diff < -0.1) tone = "behind";
                              else if (diff > 0.1) tone = "ahead";
                              const planMarker = Math.max(
                                0,
                                Math.min(100, planned),
                              );
                              return (
                                <div
                                  className="progress-plan"
                                  title={`Actual ${actual.toFixed(
                                    1,
                                  )}% vs Planned ${planned.toFixed(
                                    1,
                                  )}% (by today) · ${completed} of ${total} processes`}
                                >
                                  <div className="progress-plan-top">
                                    <span className="progress-plan-actual">
                                      {Math.round(actual)}%
                                    </span>
                                    <span className="progress-plan-planned">
                                      / {Math.round(planned)}% plan
                                    </span>
                                    <span className="progress-plan-count">
                                      {completed}/{total}
                                    </span>
                                  </div>
                                  <div className="progress-plan-bar">
                                    <div
                                      className={`progress-plan-fill progress-plan-fill-${tone}`}
                                      style={{ width: `${pct}%` }}
                                    />
                                    <span
                                      className="progress-plan-marker"
                                      style={{ left: `${planMarker}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            })()}
                          </td>
                          <td className="equipment-status-cell">
                            {(() => {
                              const badge = getScheduleStatusBadge(
                                equipment.schedule_delay_status,
                              );
                              const magnitude =
                                getChipDelayMagnitude(equipment);
                              const chipLabel = magnitude
                                ? `${badge.label} · ${magnitude}`
                                : badge.label;
                              const statusId =
                                equipment?.equipment_id ?? equipment?.id;
                              return (
                                <div className="equipment-status-stack">
                                  <button
                                    type="button"
                                    className={`equipment-delay-badge equipment-delay-badge-${badge.tone}`}
                                    aria-label={`Status details: ${chipLabel}`}
                                    onMouseEnter={(e) =>
                                      openStatusPopup(statusId, e)
                                    }
                                    onMouseLeave={(e) =>
                                      handleStatusChipMouseLeave(statusId, e)
                                    }
                                    onFocus={(e) =>
                                      openStatusPopup(statusId, e)
                                    }
                                    onClick={(e) =>
                                      toggleStatusPopup(statusId, e)
                                    }
                                  >
                                    <span
                                      className={`equipment-delay-dot equipment-delay-dot-${badge.tone}`}
                                    />
                                    <span>{chipLabel}</span>
                                  </button>
                                </div>
                              );
                            })()}
                          </td>
                          <td className="equipment-drawings-col-cell">
                            {Array.isArray(equipment.files) &&
                            equipment.files.length > 0 ? (
                              <div className="equipment-drawings-cell">
                                {(() => {
                                  const file = equipment.files[0];
                                  const fileLabel = getDrawingLabel(file);
                                  const extraCount =
                                    (equipment.total_files_count || 0) - 1;
                                  return (
                                    <>
                                      <button
                                        type="button"
                                        className="equipment-drawing-pill"
                                        title={fileLabel}
                                        onClick={() =>
                                          openEquipmentDrawingPreview(equipment)
                                        }
                                      >
                                        <LuFileText size={13} />
                                        <span className="equipment-drawing-name">
                                          {fileLabel}
                                        </span>
                                      </button>
                                      {equipment.has_more_files &&
                                        extraCount > 0 && (
                                          <button
                                            type="button"
                                            className="equipment-drawing-more"
                                            title="View all drawings"
                                            onClick={() =>
                                              openAllDrawingsModal(equipment)
                                            }
                                          >
                                            +{extraCount} more
                                          </button>
                                        )}
                                    </>
                                  );
                                })()}
                              </div>
                            ) : (
                              <span className="equipment-drawing-empty">
                                No drawing
                              </span>
                            )}
                          </td>
                          <td>
                            <div className="equipment-actions-inline">
                              <Tooltip title="View flanges">
                                <button
                                  type="button"
                                  className="equipment-action-icon-btn"
                                  onClick={() => handleOpenFlangeModal(equipment)}
                                >
                                  <img
                                    src="/assests/icons/all-flanges.png"
                                    alt="View flanges"
                                    className="associated-flanges-icon"
                                  />
                                </button>
                              </Tooltip>

                              {hasAnyQRCode() && equipment.qr_code ? (
                                <Tooltip title="QR Code">
                                  <button
                                    type="button"
                                    className="equipment-action-icon-btn"
                                    onClick={() => handleViewQRCode(equipment)}
                                  >
                                    <LiaQrcodeSolid style={{ fontSize: "19px" }} />
                                  </button>
                                </Tooltip>
                              ) : null}

                              <Tooltip title="Ticket">
                                <button
                                  type="button"
                                  className="equipment-action-icon-btn"
                                  onClick={() => {
                                    setSelectedEquipmentId(equipment.id);
                                    setShowTicketModal(true);
                                  }}
                                >
                                  <LuTicketSlash size={18} />
                                </button>
                              </Tooltip>

                              <Tooltip title="View Tickets">
                                <button
                                  type="button"
                                  className="equipment-action-icon-btn"
                                  onClick={() =>
                                    navigate(
                                      `/tickets?equipment_id=${encodeURIComponent(
                                        equipment.id,
                                      )}&equipment_label=${encodeURIComponent(
                                        equipment.equipment_tag_number || "",
                                      )}`,
                                    )
                                  }
                                >
                                  <LuTickets size={18} />
                                </button>
                              </Tooltip>

                              <Tooltip title="View Details">
                                <button
                                  type="button"
                                  className="equipment-action-icon-btn"
                                  onClick={() =>
                                    navigate(`/equipment-details/${equipment.id}`, {
                                      state: { from: "equipment-list" },
                                    })
                                  }
                                >
                                  <IoEye style={{ fontSize: "19px" }} />
                                </button>
                              </Tooltip>

                              {canManageDrawings && (
                                <Tooltip title="Upload Drawing">
                                  <button
                                    type="button"
                                    className="equipment-action-icon-btn"
                                    onClick={() =>
                                      openDrawingUploadModal(equipment)
                                    }
                                  >
                                    <CloudUploadIcon
                                      style={{ fontSize: "19px" }}
                                    />
                                  </button>
                                </Tooltip>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        No data available
                      </TableCell>
                    </TableRow>
                  )}
                </tbody>
              </table>
            </div>
          <ToastContainer
            containerId="main"
            position="top-right"
            autoClose={4000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick={false} // prevent click-bubble close race
            pauseOnFocusLoss={false} // avoid focus loss during download
            draggable
            pauseOnHover
            theme="light"
          />
          <Popover
            open={Boolean(activeStatusId != null && statusAnchorPosition)}
            anchorReference="anchorPosition"
            anchorPosition={statusAnchorPosition || { top: 0, left: 0 }}
            onClose={closeStatusCard}
            disableAutoFocus
            disableEnforceFocus
            disableRestoreFocus
            disableScrollLock
            container={statusPopoverContainer}
            disablePortal={false}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{
              backdrop: {
                invisible: true,
                sx: { pointerEvents: "none" },
              },
            }}
            PaperProps={{
              className: "equipment-status-popover-paper",
              onMouseEnter: handleStatusCardMouseEnter,
              onMouseLeave: handleStatusCardMouseLeave,
              sx: {
                bgcolor: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                boxShadow: "0 14px 30px rgba(15, 23, 42, 0.2)",
                overflow: "hidden",
                pointerEvents: "auto",
              },
            }}
            sx={{
              zIndex: 2500,
              // Let mouse events pass through to the chip underneath
              pointerEvents: "none",
            }}
          >
            {activeStatusEquipment && (
              <div className="equipment-status-popover">
                <div className="equipment-status-popover-head">
                  <p className="equipment-status-popover-title">
                    Status Overview
                  </p>
                  {(() => {
                    const badge = getScheduleStatusBadge(
                      activeStatusEquipment.schedule_delay_status,
                    );
                    return (
                      <span
                        className={`equipment-delay-badge equipment-delay-badge-${badge.tone} equipment-delay-badge-compact`}
                      >
                        <span
                          className={`equipment-delay-dot equipment-delay-dot-${badge.tone}`}
                        />
                        <span>{badge.label}</span>
                      </span>
                    );
                  })()}
                </div>

                <div className="equipment-status-popover-section">
                  <p className="equipment-status-popover-label">
                    Last Completed Process
                  </p>
                  <p className="equipment-status-popover-value">
                    {activeStatusEquipment.last_process || "N/A"}
                  </p>
                </div>

                <div className="equipment-status-popover-grid">
                  <div className="equipment-status-popover-metric">
                    <p className="equipment-status-popover-label">
                      Duration vs Plan
                    </p>
                    <p className="equipment-status-popover-number">
                      {formatDelayHours(activeStatusEquipment.process_delay_hours)}
                    </p>
                    <p
                      className={`equipment-status-popover-sub ${getStatusTextClass(
                        activeStatusEquipment.process_delay_status,
                      )}`}
                    >
                      {activeStatusEquipment.process_delay_status || "N/A"}
                    </p>
                  </div>

                  <div className="equipment-status-popover-metric">
                    <p className="equipment-status-popover-label">
                      Finish vs Schedule
                    </p>
                    <p className="equipment-status-popover-number">
                      {formatDelayHours(activeStatusEquipment.schedule_delay_hours)}
                    </p>
                    <p
                      className={`equipment-status-popover-sub ${getStatusTextClass(
                        activeStatusEquipment.schedule_delay_status,
                      )}`}
                    >
                      {activeStatusEquipment.schedule_delay_status || "N/A"}
                    </p>
                  </div>
                </div>

                {activeStatusEquipment.schedule_delay_status === "Delayed" && (
                  <div className="equipment-status-popover-alert">
                    {`⚠ Project schedule is delayed by ${formatDelayHours(activeStatusEquipment.schedule_delay_hours)}`}
                  </div>
                )}
              </div>
            )}
          </Popover>
          {showTicketModal && (
            <Dialog
              open={showTicketModal}
              onClose={handleCloseTicketModal}
              maxWidth="sm"
              fullWidth
              PaperProps={{
                className: "ticket-modal-paper",
                sx: {
                  borderRadius: 3,
                  boxShadow: "0 24px 60px rgba(15, 23, 42, 0.18)",
                  overflow: "hidden",
                },
              }}
            >
              <DialogTitle
                sx={{
                  pb: 1.75,
                  pt: 2.25,
                  px: 3,
                  borderBottom: "1px solid #edf0f5",
                  background:
                    "linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)",
                  position: "relative",
                }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#111827",
                      fontWeight: 700,
                      fontSize: "1.08rem",
                      lineHeight: 1.3,
                    }}
                  >
                    Create Ticket
                  </Typography>
                </Box>
                <IconButton
                  onClick={handleCloseTicketModal}
                  aria-label="Close ticket modal"
                  sx={{
                    position: "absolute",
                    right: 16,
                    top: 14,
                    color: "#64748b",
                    border: "1px solid #e5e7eb",
                    width: 34,
                    height: 34,
                    "&:hover": {
                      color: "#111827",
                      bgcolor: "#f8fafc",
                      borderColor: "#cbd5e1",
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </DialogTitle>

              <DialogContent sx={{ px: 3, py: 2.5, bgcolor: "#fbfcff" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.25 }}>
                  <TicketModalTextFields
                    titleRef={ticketTitleRef}
                    descriptionRef={ticketDescriptionRef}
                    titleError={ticketErrors.title}
                    onClearTitleError={() =>
                      setTicketErrors((current) => ({
                        ...current,
                        title: undefined,
                      }))
                    }
                  />

                  {/* Ticket Type */}
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ mb: 0.75, fontWeight: 500, fontSize: "0.85rem" }}
                    >
                      Ticket Type <span style={{ color: "#d32f2f" }}>*</span>
                    </Typography>

                    <MUISelect
                      value={ticketType || ""}
                      onChange={(e) => {
                        setTicketType(e.target.value);
                        if (ticketErrors.type) {
                          setTicketErrors((current) => ({
                            ...current,
                            type: undefined,
                          }));
                        }
                      }}
                      error={Boolean(ticketErrors.type)}
                      displayEmpty
                      fullWidth
                      size="small"
                      renderValue={(selected) =>
                        selected ? (
                          selected
                        ) : (
                          <span style={{ color: "#999" }}>Select type</span>
                        )
                      }
                      sx={{
                        fontSize: "0.875rem",
                        "& .MuiSelect-select": { py: 1.25 },
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Select type</em>
                      </MenuItem>
                      <MenuItem value="Alert">Alert</MenuItem>
                      <MenuItem value="Missing Information">
                        Missing Information
                      </MenuItem>
                    </MUISelect>
                    {ticketErrors.type && (
                      <Typography
                        role="alert"
                        variant="caption"
                        sx={{
                          color: "error.main",
                          ml: 1.75,
                          mt: 0.5,
                          display: "block",
                        }}
                      >
                        {ticketErrors.type}
                      </Typography>
                    )}
                  </Box>

                  {/* Priority */}
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ mb: 0.75, fontWeight: 500, fontSize: "0.85rem" }}
                    >
                      Priority <span style={{ color: "#d32f2f" }}>*</span>
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "repeat(2, minmax(0, 1fr))",
                          sm: "repeat(4, minmax(0, 1fr))",
                        },
                        gap: 1,
                      }}
                    >
                      {TICKET_PRIORITY_OPTIONS.map((option) => {
                        const selected = ticketPriority === option.value;
                        return (
                          <Box
                            key={option.value}
                            onClick={() => {
                              setTicketPriority(option.value);
                              if (ticketErrors.priority) {
                                setTicketErrors((current) => ({
                                  ...current,
                                  priority: undefined,
                                }));
                              }
                            }}
                            sx={{
                              flex: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 0.75,
                              py: 1.15,
                              border: "1px solid",
                              borderColor: selected ? option.color : "divider",
                              borderRadius: 2,
                              cursor: "pointer",
                              bgcolor: selected
                                ? `${option.color}14`
                                : "#ffffff",
                              transition: "all 0.15s ease",
                              boxShadow: selected
                                ? `0 8px 18px ${option.color}1f`
                                : "0 1px 2px rgba(15, 23, 42, 0.04)",
                              "&:hover": {
                                borderColor: option.color,
                                transform: "translateY(-1px)",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: option.color,
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "0.85rem",
                                fontWeight: selected ? 600 : 500,
                              }}
                            >
                              {option.value}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                    {ticketErrors.priority && (
                      <Typography
                        role="alert"
                        variant="caption"
                        sx={{
                          color: "error.main",
                          ml: 1.75,
                          mt: 0.5,
                          display: "block",
                        }}
                      >
                        {ticketErrors.priority}
                      </Typography>
                    )}
                  </Box>

                  {/* Assignment */}
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        color: "text.secondary",
                        fontSize: "0.7rem",
                      }}
                    >
                      ASSIGNMENT
                    </Typography>
                    <Box
                      sx={{
                        borderBottom: "1px solid",
                        borderColor: "#e7ebf3",
                        mt: 0.75,
                        mb: 1.5,
                      }}
                    />

                    <Box sx={{ mb: 1.5 }}>
                      <Typography
                        variant="body2"
                        sx={{ mb: 0.75, fontWeight: 500, fontSize: "0.85rem" }}
                      >
                        Assign to people
                      </Typography>
                      <Autocomplete
                        multiple
                        size="small"
                        disablePortal
                        slotProps={TICKET_AUTOCOMPLETE_SLOT_PROPS}
                        options={assignableUsers}
                        value={ticketAssigneeUsers}
                        onChange={(e, value) => setTicketAssigneeUsers(value)}
                        filterOptions={filterTicketAssigneeUsers}
                        getOptionLabel={(option) =>
                          option?.name
                            ? `${option.name} ${getTicketUserDepartmentLabel(option)}`
                            : ""
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        disableCloseOnSelect
                        loading={assignablesLoading}
                        noOptionsText="No project members found"
                        renderOption={(props, option, { selected }) => {
                          const { key, ...optionProps } = props;
                          const departments = getTicketUserDepartments(option);
                          return (
                            <li key={key || option.id} {...optionProps}>
                              <Checkbox
                                icon={
                                  <CheckBoxOutlineBlankIcon fontSize="small" />
                                }
                                checkedIcon={<CheckBoxIcon fontSize="small" />}
                                checked={selected}
                                size="small"
                                sx={{
                                  mr: 1,
                                  color: "#64748b",
                                  "&.Mui-checked": { color: "#991b1b" },
                                }}
                              />
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "#111827",
                                    fontSize: "0.86rem",
                                    fontWeight: 600,
                                    lineHeight: 1.25,
                                  }}
                                >
                                  {option.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#64748b",
                                    display: "block",
                                    fontSize: "0.72rem",
                                    lineHeight: 1.25,
                                    mt: 0.25,
                                  }}
                                >
                                  {departments.length
                                    ? getTicketUserDepartmentText(option)
                                    : "No department"}
                                </Typography>
                              </Box>
                            </li>
                          );
                        }}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option.id}
                              label={getTicketUserChipLabel(option)}
                              size="small"
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Search team members..."
                          />
                        )}
                      />
                    </Box>

                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ mb: 0.75, fontWeight: 500, fontSize: "0.85rem" }}
                      >
                        Assign to departments
                      </Typography>
                      <Autocomplete
                        multiple
                        size="small"
                        disablePortal
                        slotProps={TICKET_AUTOCOMPLETE_SLOT_PROPS}
                        options={assignableDepartments}
                        value={ticketAssigneeDepartments}
                        onChange={(e, value) =>
                          setTicketAssigneeDepartments(
                            withMandatoryDepartments(
                              value,
                              assignableDepartments,
                            ),
                          )
                        }
                        filterOptions={filterTicketDepartments}
                        getOptionLabel={(option) => option?.name || ""}
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        getOptionDisabled={(option) =>
                          Boolean(option.mandatory)
                        }
                        disableCloseOnSelect
                        loading={assignablesLoading}
                        noOptionsText="No departments found"
                        renderOption={(props, option, { selected }) => (
                          <li {...props} key={option.id}>
                            <Checkbox
                              icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                              checkedIcon={<CheckBoxIcon fontSize="small" />}
                              checked={selected}
                              size="small"
                              sx={{
                                mr: 1,
                                color: "#64748b",
                                "&.Mui-checked": { color: "#991b1b" },
                              }}
                            />
                            {option.name}
                            {option.mandatory && (
                              <Typography
                                component="span"
                                sx={{
                                  ml: "auto",
                                  fontSize: "0.7rem",
                                  color: "#991b1b",
                                }}
                              >
                                Always assigned
                              </Typography>
                            )}
                          </li>
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => {
                            const tagProps = getTagProps({ index });
                            if (option.mandatory) {
                              // Locked chip: onDelete overridden to undefined so
                              // there's no remove button — the planning team
                              // can't be removed (backend enforces it too).
                              return (
                                <Chip
                                  {...tagProps}
                                  onDelete={undefined}
                                  key={option.id}
                                  label={option.name}
                                  size="small"
                                  icon={
                                    <LockIcon sx={{ fontSize: "0.8rem" }} />
                                  }
                                  title="Planning is always assigned and can't be removed"
                                  sx={{
                                    "& .MuiChip-icon": { color: "#991b1b" },
                                  }}
                                />
                              );
                            }
                            return (
                              <Chip
                                {...tagProps}
                                key={option.id}
                                label={option.name}
                                size="small"
                              />
                            );
                          })
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Search departments..."
                          />
                        )}
                      />
                    </Box>
                  </Box>

                  {/* Attachments */}
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        color: "text.secondary",
                        fontSize: "0.7rem",
                      }}
                    >
                      ATTACHMENTS
                    </Typography>
                    <Box
                      sx={{
                        borderBottom: "1px solid",
                        borderColor: "#e7ebf3",
                        mt: 0.75,
                        mb: 1.5,
                      }}
                    />

                    <Box
                      component="label"
                      onDragEnter={handleDragEnter}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      sx={{
                        display: "block",
                        width: "100%",
                        p: 2,
                        border: "2px dashed",
                        borderColor: isDragging ? "#7c2d12" : "#d6dbe6",
                        borderRadius: 2.5,
                        textAlign: "center",
                        cursor: "pointer",
                        bgcolor: isDragging ? "#fff7ed" : "#ffffff",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: "#991b1b",
                          bgcolor: "#fffafa",
                        },
                      }}
                    >
                      <CloudUploadIcon
                        sx={{
                          fontSize: 32,
                          color: "#991b1b",
                          mb: 0.5,
                          transform: isDragging ? "scale(1.1)" : "scale(1)",
                          transition: "transform 0.2s ease",
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.8rem",
                          mb: 0.25,
                          fontWeight: isDragging ? 600 : 400,
                        }}
                      >
                        {isDragging ? (
                          "Drop files here"
                        ) : (
                          <>
                            Drag &amp; drop files here or{" "}
                            <span
                              style={{ color: "#991b1b", fontWeight: 700 }}
                            >
                              browse
                            </span>
                          </>
                        )}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "0.7rem", color: "text.secondary" }}
                      >
                        images &amp; video up to 5 MB
                      </Typography>
                      <input
                        type="file"
                        multiple
                        accept="image/jpeg,image/png,image/jpg,video/mp4,video/quicktime"
                        onChange={(e) => {
                          const files = e.target.files;
                          if (files && files.length > 0) {
                            setTicketAttachments((prev) => [
                              ...prev,
                              ...Array.from(files),
                            ]);
                          }
                          e.target.value = "";
                        }}
                        hidden
                      />
                    </Box>

                    {ticketAttachments.length > 0 && (
                      <Box
                        sx={{
                          mt: 1.5,
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {ticketAttachments.map((file, index) => (
                          <Box
                            key={`${file.name}-${index}`}
                            sx={{
                              width: "100%",
                              p: 1.25,
                              border: "1px solid",
                              borderColor: "success.main",
                              borderRadius: 1,
                              bgcolor: "#f1f8f4",
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 1,
                                bgcolor: "success.main",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              {file.type.startsWith("image/") ? (
                                <ImageIcon
                                  sx={{ color: "white", fontSize: 18 }}
                                />
                              ) : (
                                <InsertDriveFileIcon
                                  sx={{ color: "white", fontSize: 18 }}
                                />
                              )}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500, fontSize: "0.8rem" }}
                                noWrap
                              >
                                {file.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.7rem",
                                  color: "text.secondary",
                                }}
                              >
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() =>
                                setTicketAttachments((prev) =>
                                  prev.filter((_, i) => i !== index),
                                )
                              }
                              sx={{
                                color: "text.secondary",
                                "&:hover": {
                                  color: "error.main",
                                  bgcolor: "rgba(211, 47, 47, 0.08)",
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              </DialogContent>

              <DialogActions
                sx={{
                  px: 3,
                  py: 1.75,
                  borderTop: "1px solid #edf0f5",
                  bgcolor: "#ffffff",
                  gap: 1.25,
                }}
              >
                <Button
                  onClick={handleCloseTicketModal}
                  variant="outlined"
                  disabled={ticketLoading}
                  size="small"
                  sx={{
                    minWidth: 90,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.875rem",
                    borderRadius: 2,
                    borderColor: "#cbd5e1",
                    color: "#334155",
                    px: 3,
                    py: 1,
                    "&:hover": {
                      borderColor: "#94a3b8",
                      bgcolor: "#f8fafc",
                    },
                  }}
                >
                  Cancel
                </Button>

                <Button
                  onClick={raiseTicket}
                  variant="contained"
                  disabled={ticketLoading}
                  size="small"
                  sx={{
                    minWidth: 150,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.875rem",
                    borderRadius: 2,
                    py: 1,
                    bgcolor: "#991b1b",
                    boxShadow: "0 10px 22px rgba(153, 27, 27, 0.22)",
                    "&:hover": {
                      bgcolor: "#7f1d1d",
                      boxShadow: "0 12px 26px rgba(153, 27, 27, 0.26)",
                    },
                  }}
                >
                  {ticketLoading ? "Submitting..." : "Create Ticket"}
                </Button>
              </DialogActions>
            </Dialog>
          )}
          <div className="equipment-pagination-footer">
            <PaginationFooter
              totalRecords={totalRecords}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              setCurrentPage={setCurrentPage}
              setItemsPerPage={setItemsPerPage}
            />
          </div>
          </div>
          </div>
        </div>
      </div>
      {/* Import Modal */}
      {showAreaPopup && (
        <Dialog
          open={showAreaPopup}
          onClose={handleCloseAreaPopup}
          maxWidth={false}
          fullWidth={false}
          PaperProps={{ className: "area-filter-modal-paper" }}
        >
          <DialogContent sx={{ p: 0 }}>
            <div className="area-filter-modal">
              <div className="area-filter-header">
                <div className="area-filter-title-wrap">
                  <div className="area-filter-icon">
                    <FiFilter size={18} />
                  </div>
                  <div>
                    <h2 className="area-filter-title">Area Report Search</h2>
                    <p className="area-filter-subtitle">
                      Configure search parameters for area-based filtering.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="area-filter-close"
                  onClick={handleCloseAreaPopup}
                  aria-label="Close area filter"
                >
                  <IoIosClose size={22} />
                </button>
              </div>

              <div className="area-filter-body">
                <div className="area-filter-field">
                  <label htmlFor="area-search-type">Search Type</label>
                  <select
                    id="area-search-type"
                    className="area-filter-select"
                    value={searchType}
                    onChange={(e) => setSearchType(e.target.value)}
                  >
                    <option value="contains">Contains</option>
                    <option value="starts_with">Starts With</option>
                    <option value="ends_with">Ends With</option>
                  </select>
                </div>

                <div className="area-filter-field">
                  <label htmlFor="area-search-value">Search Value</label>
                  <input
                    id="area-search-value"
                    className="area-filter-input"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder="Enter search text"
                  />
                  <p className="area-filter-help">
                    Enter the text to search for in the area field
                  </p>
                </div>

                <div className="area-filter-field">
                  <label htmlFor="area-position-start">
                    Position Start (Optional)
                  </label>
                  <input
                    id="area-position-start"
                    className={`area-filter-input ${
                      positionStartInvalid ? "area-filter-input-error" : ""
                    }`}
                    value={positionStart}
                    onChange={(e) => handlePositionStartChange(e.target.value)}
                    placeholder="Starting position"
                    inputMode="numeric"
                    pattern="[1-9][0-9]*"
                  />
                  {positionStartInvalid && (
                    <p className="area-filter-error">
                      Position Start must be greater than 0
                    </p>
                  )}
                </div>

                <div className="area-filter-field">
                  <label htmlFor="area-position-end">Position End (Optional)</label>
                  <input
                    id="area-position-end"
                    className={`area-filter-input ${
                      positionEndInvalid || areaRangeInvalid
                        ? "area-filter-input-error"
                        : ""
                    }`}
                    value={positionEnd}
                    onChange={(e) => handlePositionEndChange(e.target.value)}
                    placeholder="Ending position"
                    inputMode="numeric"
                    pattern="[1-9][0-9]*"
                  />
                  {positionEndInvalid && (
                    <p className="area-filter-error">
                      Position End must be greater than 0
                    </p>
                  )}
                  {!positionStartInvalid && !positionEndInvalid && areaRangeInvalid && (
                    <p className="area-filter-error">
                      Position End must be greater than or equal to Position Start
                    </p>
                  )}
                </div>
              </div>

              <div className="area-filter-footer">
                {isAreaFilterActive && (
                  <button
                    type="button"
                    className="area-filter-clear-inline"
                    onClick={clearAreaFilter}
                  >
                    <IoIosClose size={16} />
                    Clear Filter
                  </button>
                )}
                <button
                  type="button"
                  className="area-filter-cancel"
                  onClick={handleCloseAreaPopup}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="area-filter-apply"
                  onClick={handleAreaFilterApply}
                  disabled={isAreaApplyDisabled}
                >
                  Apply Filter
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Import Modal */}
      {isAdmin && showPopup && (
        <div className="overlay" onClick={handleCloseImportPopup}>
          <div
            className="popup-import equi-popup-import brand-import-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ minHeight: "20vh !important" }}
          >
            <div className="d-flex justify-content-between align-items-center mb-3 brand-import-header">
              <h4 className="mb-0 brand-import-title">Upload Excel</h4>
              <button
                type="button"
                className="btn-close brand-modal-close"
                aria-label="Close"
                onClick={handleCloseImportPopup}
                style={{
                  width: "28px",
                  height: "28px",
                  background: "transparent",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  outline: "0",
                  padding: "0",
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#f0f0f0")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "transparent")
                }
              >
                <IoIosClose size={24} color="#666" />
              </button>
            </div>

            <div
              className="drop-zone dotted-border border-secondary text-center mt-3 brand-drop-zone"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer.files?.[0];
                if (file) setSelectedFile(file);
              }}
            >
              <p className="mb-2 fs-5">📂 Drag & drop your Excel file here</p>
              <p className="mb-3 text-muted">or</p>

              <input
                ref={uploadInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                className="form-control file-input"
                disabled={loading}
              />
            </div>

            <div className="btnspace mt-3 d-flex justify-content-end">
              <button
                className="btn mx-2 brand-btn-primary"
                onClick={handleImportEquipment}
                disabled={loading || !selectedFile}
              >
                {loading ? "Importing..." : "Import"}
              </button>
              <button
                onClick={handleCloseImportPopup}
                className="btn brand-btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <Dialog
        open={Boolean(resourceMappingImport)}
        onClose={loading ? undefined : () => {
          setResourceMappingImport(null);
          setResourceMappingSelections({});
          setSelectedFile(null);
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ className: "jecon-dialog equipment-resource-mapping-dialog" }}
      >
        <DialogTitle className="equipment-resource-mapping-title">
          <span className="equipment-resource-mapping-title-icon"><InsertDriveFileIcon /></span>
          <span><strong>Map Excel resources</strong><small>Equipment import</small></span>
          <IconButton
            className="equipment-resource-mapping-close"
            onClick={() => {
              setResourceMappingImport(null);
              setResourceMappingSelections({});
              setSelectedFile(null);
            }}
            disabled={loading}
            aria-label="Close"
          ><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers className="equipment-resource-mapping-content">
          <Typography className="equipment-resource-mapping-help">
            Select a department for each resource found in the Excel file.
          </Typography>
          <Box className="equipment-resource-mapping-list">
            {(resourceMappingImport?.resources || []).map((resource) => (
              <Box
                key={resource.normalized_source_name}
                className={`equipment-resource-mapping-row${resourceMappingSelections[resource.normalized_source_name] ? " is-mapped" : ""}`}
              >
                <Typography className="equipment-resource-mapping-name">
                  {resource.source_resource_name}
                </Typography>
                <Autocomplete
                    fullWidth
                    size="small"
                    disabled={loading}
                    className="equipment-resource-mapping-select"
                    options={resourceMappingImport?.departments || []}
                    value={(resourceMappingImport?.departments || []).find(
                      (department) => department.id === resourceMappingSelections[resource.normalized_source_name],
                    ) || null}
                    getOptionLabel={(department) => department.department_name || ""}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onChange={(_event, department) => setResourceMappingSelections((current) => ({
                      ...current,
                      [resource.normalized_source_name]: department?.id || "",
                    }))}
                    noOptionsText="No departments found"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search departments..."
                        aria-label={`Department for ${resource.source_resource_name}`}
                      />
                    )}
                  />
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions className="equipment-resource-mapping-actions">
          <Button
            onClick={() => {
              setResourceMappingImport(null);
              setResourceMappingSelections({});
              setSelectedFile(null);
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            className="equipment-resource-mapping-submit"
            onClick={handleSaveResourceMappings}
            disabled={loading || !(resourceMappingImport?.resources || []).length}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {loading ? "Importing..." : "Save mappings & import"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Modal */}
      {showQRModal && selectedQRCodeEquipment && (
        <div className="modal-overlay" onClick={() => setShowQRModal(false)}>
          <div
            className="shared-qr-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="shared-qr-modal-header">
              <h4 className="shared-qr-modal-title">Equipment QR Code</h4>
            </div>

            <div className="text-center">
              <div className="shared-qr-equipment-info">
                <div className="shared-qr-info-row">
                  <span className="shared-qr-info-label">Equipment No:</span>{" "}
                  <span className="shared-qr-info-value">
                    {selectedQRCodeEquipment.equipment_tag_number || "N/A"}
                  </span>
                </div>
                {selectedQRCodeEquipment.equipment_group && (
                  <div className="shared-qr-info-row">
                    <span className="shared-qr-info-label">Group:</span>{" "}
                    <span>{selectedQRCodeEquipment.equipment_group}</span>
                  </div>
                )}
              </div>

              <div className="shared-qr-code-wrap">
                {selectedQRCodeEquipment.qr_code ? (
                  <img
                    src={`data:image/png;base64,${selectedQRCodeEquipment.qr_code}`}
                    alt="QR Code"
                    className="shared-qr-code-image"
                  />
                ) : (
                  <div className="shared-qr-code-empty">
                    No QR Code Available
                  </div>
                )}
              </div>

              {selectedQRCodeEquipment.qr_code && (
                <div className="shared-qr-modal-footer">
                  <button
                    className="btn btn-success"
                    onClick={() =>
                      handlePrintQRCode(selectedQRCodeEquipment)
                    }
                  >
                    Print
                  </button>

                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowQRModal(false)}
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Modal open={isFlangeModalOpen} onClose={handleCloseFlangeModal}>
        <Box className="flange-modal-content">
          <Box className="flange-modal-header">
            <Typography variant="h6" component="h3">
              Associated Flanges -{" "}
              {selectedEquipment?.equipment_number || "N/A"}
            </Typography>
            <IconButton onClick={handleCloseFlangeModal} size="small">
              <IoIosClose size={24} />
            </IconButton>
          </Box>

          <Box className="flange-modal-body">
            {flangesLoading ? (
              <div className="flange-modal-loading">
                <CircularProgress size={26} />
                <p>Loading flanges...</p>
              </div>
            ) : flangesError ? (
              <div className="flange-modal-error">
                <p>{flangesError}</p>
                <button
                  className="btn btn-outline-primary"
                  onClick={() => fetchAssociatedFlanges(selectedEquipment?.id)}
                >
                  <MdOutlineRefresh size={18} /> Retry
                </button>
              </div>
            ) : flanges.length === 0 ? (
              <div className="flange-modal-empty">
                No flanges found for this equipment.
              </div>
            ) : (
              <div className="flange-modal-table-wrap">
                <table className="flange-modal-table">
                  <thead>
                    <tr>
                      <th>Sr No</th>
                      <th>Flange Number</th>
                      <th>Status</th>
                      <th>Open</th>
                      <th>QR Code</th>
                    </tr>
                  </thead>
                  <tbody>
                    {flanges.map((flange, index) => {
                      const flangeId = flange?.flange_id || flange?.id;

                      return (
                        <tr key={flangeId || index}>
                          <td>{index + 1}</td>
                          <td>
                            {flangeId ? (
                              <button
                                type="button"
                                className="flange-open-link-btn"
                                onClick={() =>
                                  handleOpenFlangeInAllFlanges(flange)
                                }
                                title={`Open ${flange.flange_number || "flange"} in All Flanges`}
                                aria-label={`Open ${flange.flange_number || "flange"} in All Flanges`}
                              >
                                {flange.flange_number || "N/A"}
                              </button>
                            ) : (
                              flange.flange_number || "N/A"
                            )}
                          </td>
                          <td>
                            <span
                              className={`flange-status-chip ${getFlangeStatusClass(flange.status)}`}
                            >
                              {flange.status || "N/A"}
                            </span>
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {flangeId ? (
                              <button
                                type="button"
                                className="equipment-action-btn flange-open-icon-btn"
                                title={`Open ${flange.flange_number || "flange"} in All Flanges`}
                                aria-label={`Open ${flange.flange_number || "flange"} in All Flanges`}
                                onClick={() =>
                                  handleOpenFlangeInAllFlanges(flange)
                                }
                              >
                                <IoEye style={{ fontSize: "18px" }} />
                              </button>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {hasValidQrCode(flange.qr_code) ? (
                              <button
                                type="button"
                                className="equipment-action-btn flange-qr-icon-btn"
                                title="View QR Code"
                                aria-label={`View QR Code for ${
                                  flange.flange_number || "flange"
                                }`}
                                onClick={() => handleOpenFlangeQRModal(flange)}
                              >
                                <LiaQrcodeSolid style={{ fontSize: "20px" }} />
                              </button>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Box>
        </Box>
      </Modal>

      <Modal
        open={showFlangeQRModal && !!selectedFlangeQR}
        onClose={handleCloseFlangeQRModal}
        sx={{ zIndex: 1400 }}
      >
        <Box className="flange-qr-modal-content">
          <div className="d-flex justify-content-center align-items-center mb-2">
            <h4 className="mb-0">QR Code</h4>
          </div>

          {selectedFlangeQR?.flange_number && (
            <p className="flange-qr-subtitle">
              {selectedFlangeQR.flange_number}
            </p>
          )}

          <div className="d-flex justify-content-center mb-3">
            {selectedFlangeQR?.qr_code && (
              <img
                src={getQrImageSrc(selectedFlangeQR.qr_code)}
                alt="Flange QR Code"
                width={280}
                height={280}
                style={{ padding: "8px", objectFit: "contain" }}
              />
            )}
          </div>

          <div className="mt-2 d-flex justify-content-center">
            <button
              type="button"
              className="btn btn-success me-2"
              onClick={handlePrintFlangeQRCode}
            >
              Print
            </button>

            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCloseFlangeQRModal}
            >
              Close
            </button>
          </div>
        </Box>
      </Modal>

      <EquipmentDrawingUploadModal
        isOpen={drawingUploadModalOpen}
        onClose={closeDrawingUploadModal}
        equipment={drawingUploadEquipment}
        onUploaded={handleDrawingUploaded}
      />

      <Dialog
        open={showDrawingsListModal}
        onClose={() => setShowDrawingsListModal(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ className: "jecon-dialog" }}
      >
        <DialogTitle>All Drawings</DialogTitle>
        <DialogContent dividers>
          {drawingsListLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "24px",
              }}
            >
              <CircularProgress size={26} />
            </div>
          ) : selectedDrawingFiles.length > 0 ? (
            <div className="equipment-drawings-list">
              {selectedDrawingFiles.map((file, idx) => {
                const label = getDrawingLabel(file);
                const meta = formatDrawingMeta(file);
                return (
                  <div
                    key={file?.file_id || label}
                    className="equipment-drawing-list-row"
                  >
                    <button
                      type="button"
                      className="equipment-drawing-list-item"
                      onClick={() => {
                        setShowDrawingsListModal(false);
                        openDrawingPreviewAt(selectedDrawingFiles, idx);
                      }}
                      title={label}
                    >
                      <LuFileText size={14} />
                      <span className="equipment-drawing-list-info">
                        <span className="equipment-drawing-list-name">
                          {label}
                        </span>
                        {meta && (
                          <span className="equipment-drawing-list-meta">
                            {meta}
                          </span>
                        )}
                      </span>
                    </button>
                    {canManageDrawings && (
                      <Tooltip title="Rename drawing">
                        <button
                          type="button"
                          className="equipment-drawing-list-edit"
                          onClick={() => requestRenameDrawing(file)}
                          aria-label="Rename drawing"
                        >
                          <EditIcon style={{ fontSize: "17px" }} />
                        </button>
                      </Tooltip>
                    )}
                    {canManageDrawings && (
                      <Tooltip title="Delete drawing">
                        <button
                          type="button"
                          className="equipment-drawing-list-delete"
                          onClick={() => requestDeleteDrawing(file)}
                          aria-label="Delete drawing"
                        >
                          <DeleteIcon style={{ fontSize: "18px" }} />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No files available.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDrawingsListModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <DrawingPreviewModal
        open={previewIndex !== null}
        files={previewFiles}
        index={previewIndex ?? 0}
        onIndexChange={setPreviewIndex}
        onClose={closeDrawingPreview}
        canDelete={canManageDrawings}
        onDelete={requestDeleteDrawing}
      />

      <Dialog
        open={Boolean(drawingDeleteTarget)}
        onClose={cancelDeleteDrawing}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: "jecon-dialog" }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete drawing?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            This will permanently delete{" "}
            <strong>{getDrawingLabel(drawingDeleteTarget)}</strong>. This can't
            be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={cancelDeleteDrawing} disabled={drawingDeleteBusy}>
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteDrawing}
            variant="contained"
            color="error"
            disabled={drawingDeleteBusy}
            startIcon={
              drawingDeleteBusy ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {drawingDeleteBusy ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(drawingRenameTarget)}
        onClose={cancelRenameDrawing}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: "jecon-dialog" }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Rename drawing</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Drawing name"
            value={drawingRenameValue}
            onChange={(e) => setDrawingRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") confirmRenameDrawing();
            }}
            inputProps={{ maxLength: 255 }}
            disabled={drawingRenameBusy}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={cancelRenameDrawing} disabled={drawingRenameBusy}>
            Cancel
          </Button>
          <Button
            onClick={confirmRenameDrawing}
            variant="contained"
            disabled={drawingRenameBusy || !drawingRenameValue.trim()}
            startIcon={
              drawingRenameBusy ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
            sx={{
              backgroundColor: "#b71c1c",
              "&:hover": { backgroundColor: "#9f1919" },
            }}
          >
            {drawingRenameBusy ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.show}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ show: false, message: "", type: "" })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ transform: "translateY(40px)", marginLeft: "15px" }}
      >
        <MuiAlert
          onClose={() => setSnackbar({ show: false, message: "", type: "" })}
          severity={snackbar.type}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default Equipment;
